import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import gavilLogo from './gavil_logo.png';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import * as pdfjsLib from 'pdfjs-dist';
import ReactMarkdown from 'react-markdown';
import {
  Document as DocxDocument, Packer as DocxPacker, Paragraph as DocxParagraph, TextRun as DocxTextRun,
  Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, ImageRun as DocxImageRun,
  WidthType as DocxWidthType, VerticalAlign as DocxVerticalAlign, HeadingLevel as DocxHeadingLevel,
} from 'docx';
import './App.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const PDF_SCALE = 1.5;
// Stable empty array so PdfPage effects don't re-fire for pages with no highlights.
const NO_HIGHLIGHTS = [];

function PdfPage({ pdf, pageNum, scale, highlights, emphasisHighlights, searchHighlights, highlightColor, coloredHighlights, scrollRoot, onRendered }) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const [dims, setDims] = useState(null);
  const renderedRef = useRef(false);
  const renderTaskRef = useRef(null);

  // 1. Fetch page dimensions, capping scale so the page fits the container width.
  useEffect(() => {
    let cancelled = false;
    pdf.getPage(pageNum).then((page) => {
      if (cancelled) return;
      const baseVp = page.getViewport({ scale: 1 });
      let fitScale = scale;
      if (scrollRoot) {
        const availableWidth = scrollRoot.clientWidth - 32; // 16px padding each side
        if (baseVp.width * scale > availableWidth) {
          fitScale = availableWidth / baseVp.width;
        }
      }
      const vp = page.getViewport({ scale: fitScale });
      setDims({ width: vp.width, height: vp.height, scale: fitScale });
    });
    return () => { cancelled = true; };
  }, [pdf, pageNum, scale, scrollRoot]);

  // Reset rendered flag when search or colored highlights change so the page redraws.
  useEffect(() => { renderedRef.current = false; }, [searchHighlights]);
  useEffect(() => { renderedRef.current = false; }, [coloredHighlights]);

  // 2. Once we know the size, observe visibility and render when in view.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !dims || renderedRef.current) return;

    let cancelled = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || renderedRef.current) return;
        renderedRef.current = true;
        observer.disconnect();
        renderPage();
      },
      { root: scrollRoot, rootMargin: '400px' },
    );
    observer.observe(el);

    async function renderPage() {
      // Cancel any in-progress render on this canvas before starting a new one.
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      const page = await pdf.getPage(pageNum);
      if (cancelled) return;
      const vp = page.getViewport({ scale: dims.scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = vp.width;
      canvas.height = vp.height;

      const ctx = canvas.getContext('2d');
      const task = page.render({ canvasContext: ctx, viewport: vp });
      renderTaskRef.current = task;
      try {
        await task.promise;
      } catch (e) {
        if (e?.name === 'RenderingCancelledException') return;
        throw e;
      }
      renderTaskRef.current = null;
      if (cancelled) return;

      // Draw highlight rectangles (status color if provided, else light blue)
      if (highlights.length > 0) {
        ctx.fillStyle = highlightColor || 'rgba(100, 180, 255, 0.2)';
        for (const bbox of highlights) {
          ctx.fillRect(
            bbox.left * vp.width,
            bbox.top * vp.height,
            bbox.width * vp.width,
            bbox.height * vp.height,
          );
        }
      }
      // Draw emphasis highlights (darker blue for evidence paragraph)
      if (emphasisHighlights && emphasisHighlights.length > 0) {
        ctx.fillStyle = 'rgba(50, 130, 240, 0.25)';
        for (const bbox of emphasisHighlights) {
          ctx.fillRect(
            bbox.left * vp.width,
            bbox.top * vp.height,
            bbox.width * vp.width,
            bbox.height * vp.height,
          );
        }
      }
      // Draw search match highlights (purple)
      if (searchHighlights && searchHighlights.length > 0) {
        ctx.fillStyle = 'rgba(160, 80, 220, 0.35)';
        for (const bbox of searchHighlights) {
          ctx.fillRect(
            bbox.left * vp.width,
            bbox.top * vp.height,
            bbox.width * vp.width,
            bbox.height * vp.height,
          );
        }
      }
      // Draw per-claim status colored highlights (all claims simultaneously)
      if (coloredHighlights && coloredHighlights.length > 0) {
        for (const { bbox, color } of coloredHighlights) {
          ctx.fillStyle = color;
          ctx.fillRect(
            bbox.left * vp.width,
            bbox.top * vp.height,
            bbox.width * vp.width,
            bbox.height * vp.height,
          );
        }
      }
      if (onRendered) onRendered(pageNum);
    }

    return () => {
      cancelled = true;
      observer.disconnect();
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [dims, scrollRoot, pdf, pageNum, scale, highlights, emphasisHighlights, searchHighlights, coloredHighlights, onRendered]);

  return (
    <div
      ref={wrapperRef}
      className="pdf-page-wrapper"
      data-page={pageNum}
      style={
        dims
          ? { width: dims.width, height: dims.height }
          : { width: 612 * scale, height: 792 * scale }
      }
    >
      <canvas ref={canvasRef} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF text-layer utilities for sub-line limitation highlighting
// ---------------------------------------------------------------------------
function _normText(str) {
  return str.toLowerCase().replace(/[\s ]+/g, ' ').replace(/-\s+/g, '').trim();
}

const _SEVERITY = { deficient: 2, concern: 1, adequate: 0 };

// Find the start of claim N's content in page text.
// Uses "N. <firstWords>" for specificity — avoids false matches on "FIG. N." or "area N."
// in spec body text where just "N. " would collide.
function _claimContentStart(pageText, claimNum, limNorm) {
  const pfx = `${claimNum}. `;
  // Try specific match: claim number + first 30 chars of limitation text.
  if (limNorm) {
    const specific = pfx + limNorm.slice(0, 30);
    const idx = pageText.indexOf(specific);
    if (idx !== -1) return idx + pfx.length;
  }
  // Fallback: bare "N. " preceded by a space (handles first-limitation case).
  let at = pageText.indexOf(pfx);
  while (at !== -1) {
    if (at === 0 || pageText[at - 1] === ' ') return at + pfx.length;
    at = pageText.indexOf(pfx, at + 1);
  }
  return -1;
}

function _matchLimitationsOnPage(items, viewport, limitations, STATUS_COLOR) {
  // Build normalised item list with 0-1 top-down coordinates.
  const normItems = items
    .filter(it => it.str && it.str.trim())
    .map(it => {
      const [, , , , x, y] = it.transform;
      const h = it.height || Math.abs(it.transform[3]) || 10;
      return {
        normStr: _normText(it.str),
        left:   x / viewport.width,
        top:    (viewport.height - y - h) / viewport.height,
        width:  (it.width || 0) / viewport.width,
        height: h / viewport.height,
      };
    });
  if (!normItems.length) return [];

  // Concatenate items into a single string; track char→item index.
  let pageText = '';
  const charToIdx = [];
  for (let i = 0; i < normItems.length; i++) {
    if (pageText) { pageText += ' '; charToIdx.push(-1); }
    const start = pageText.length;
    pageText += normItems[i].normStr;
    for (let c = start; c < pageText.length; c++) charToIdx[c] = i;
  }

  // For each text item, track the worst (highest severity) color across all limitations.
  const itemSeverity = new Array(normItems.length).fill(-1);
  const itemColor    = new Array(normItems.length).fill(null);

  for (const lim of limitations) {
    const limNorm = _normText(lim.limitation);
    const claimNum = lim.claim_number;

    // Locate the claim's region on this page using its number marker ("N. ").
    // If the claim isn't on this page, skip it — avoids prefix collisions between claims.
    const claimStart = _claimContentStart(pageText, claimNum, limNorm);
    if (claimStart === -1) continue;
    const _dbg = [2,3,11,13,15].includes(claimNum);
    if (_dbg) console.log(`[DBG claim ${claimNum}] claimStart=${claimStart} region preview: "${pageText.slice(claimStart, claimStart+80)}"`);

    // End of claim = start of the next claim that appears on this page.
    let claimEnd = pageText.length;
    for (let n = claimNum + 1; n <= claimNum + 10; n++) {
      const ns = _claimContentStart(pageText, n);
      if (ns !== -1) {
        // ns points past "n. "; back up to include the marker start
        claimEnd = ns - `${n}. `.length;
        break;
      }
    }

    // Search for the limitation text within this claim's region only.
    const region = pageText.slice(claimStart, claimEnd);
    let matchAt = -1, matchLen = 0;
    for (const len of [limNorm.length, Math.floor(limNorm.length * 0.8), 60, 40]) {
      if (len < 15) break;
      const idx = region.indexOf(limNorm.slice(0, len));
      if (idx !== -1) { matchAt = claimStart + idx; matchLen = len; break; }
    }
    if (_dbg) console.log(`[DBG claim ${claimNum}] matchAt=${matchAt} matchLen=${matchLen} lim60="${limNorm.slice(0,60)}"`);
    if (matchAt === -1) continue;

    const statusKey = (lim.status || 'concern').toLowerCase();
    const sev = _SEVERITY[statusKey] ?? 1;
    const col = STATUS_COLOR[statusKey] || STATUS_COLOR.concern;

    for (let c = matchAt; c < matchAt + matchLen && c < charToIdx.length; c++) {
      const idx = charToIdx[c];
      if (idx < 0) continue;
      if (sev > itemSeverity[idx]) { itemSeverity[idx] = sev; itemColor[idx] = col; }
    }
  }

  // Collect highlighted items, sort by position.
  const highlighted = normItems
    .map((it, i) => itemColor[i] ? { ...it, color: itemColor[i] } : null)
    .filter(Boolean)
    .sort((a, b) => a.top !== b.top ? a.top - b.top : a.left - b.left);
  if (!highlighted.length) return [];

  // Group into lines (items within 0.008 of each other in top).
  const lines = [];
  for (const it of highlighted) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(it.top - last[0].top) < 0.008) last.push(it);
    else lines.push([it]);
  }

  // Build highlight rects. Cap each line's height so adjacent lines don't overlap
  // (overlapping semi-transparent rects create a visible seam).
  const highlights = [];
  for (let li = 0; li < lines.length; li++) {
    const lineItems = lines[li].sort((a, b) => a.left - b.left);
    const lineTop    = Math.min(...lineItems.map(i => i.top));
    const lineBottom = Math.max(...lineItems.map(i => i.top + i.height));
    // Cap bottom at next line's top to eliminate seam, with a small gap.
    const nextLineTop = li + 1 < lines.length
      ? Math.min(...lines[li + 1].map(i => i.top))
      : lineBottom;
    const cappedBottom = Math.min(lineBottom, nextLineTop - 0.0005);

    let s = 0;
    while (s < lineItems.length) {
      const col = lineItems[s].color;
      let e = s;
      while (e + 1 < lineItems.length && lineItems[e + 1].color === col) e++;
      const seg = lineItems.slice(s, e + 1);
      highlights.push({
        bbox: {
          left:   Math.min(...seg.map(i => i.left)),
          top:    lineTop,
          width:  Math.max(...seg.map(i => i.left + i.width)) - Math.min(...seg.map(i => i.left)),
          height: cappedBottom - lineTop,
        },
        color: col,
      });
      s = e + 1;
    }
  }
  return highlights;
}

const _SUFF_STATUS_COLOR = {
  deficient: 'rgba(220, 50, 50, 0.38)',
  concern:   'rgba(220, 160, 0, 0.38)',
  adequate:  'rgba(50, 180, 80, 0.32)',
};

function renderCitations(text, sourcesByNumber, onSourceClick) {
  // Expand "[4, 8]" or "[4,8,12]" into "[4][8][12]"
  text = text.replace(/\[(\d+(?:\s*,\s*\d+)+)\]/g, (_, nums) =>
    nums.split(/\s*,\s*/).map((n) => `[${n}]`).join('')
  );
  // Convert citation brackets [N] into markdown links so ReactMarkdown can parse them
  text = text.replace(/\[(\d+)\]/g, (match, num) => {
    const src = sourcesByNumber[parseInt(num, 10)];
    if (src && src.filename && src.bboxes && src.bboxes.length > 0) {
      return `[\\[${num}\\]](#cite-${num})`;
    }
    return match;
  });
  return (
    <ReactMarkdown
      components={{
        a({ href, children }) {
          const m = href && href.match(/^#cite-(\d+)$/);
          if (m) {
            const src = sourcesByNumber[parseInt(m[1], 10)];
            if (src) {
              return (
                <span className="citation-link" onClick={() => onSourceClick(src, { showSummary: true })}>
                  {children}
                </span>
              );
            }
          }
          return <a href={href}>{children}</a>;
        },
        // Render paragraphs as spans to avoid nesting issues when used inside <p>
        p({ children }) { return <span className="md-paragraph">{children}</span>; },
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

const REASONING_OPTIONS = [
  { value: '', label: 'No Reasoning' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

// Upload (or reuse, by content checksum) a patent into a portfolio and track its ingest
// status. Shared by Sufficiency Check and Claim Chart's two patent slots — originally
// built for Sufficiency alone, extracted once Claim Chart needed the identical flow.
function usePatentUpload(portfolioId, authFetch) {
  const [doc, setDoc] = useState(null); // { id, name, status, progress?, error_message? } | null
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = useCallback(async (file) => {
    if (!file || !portfolioId) return;
    setUploadLoading(true);
    setUploadError(null);
    setDoc(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await authFetch(`/portfolios/${portfolioId}/documents`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data.existing_document_id) {
        // Already in the portfolio (matched by content) — reuse the ingested version.
        // If it's already 'ready', the polling effect below never runs (it skips terminal
        // statuses), so look up the real title now instead of leaving name at the raw
        // uploaded filename.
        let name = file.name;
        try {
          const listRes = await authFetch(`/portfolios/${portfolioId}/documents`, { cache: 'no-store' });
          const listData = await listRes.json();
          const existingDoc = (listData.documents || []).find((d) => d.id === data.existing_document_id);
          if (existingDoc) name = existingDoc.title || existingDoc.display_name || existingDoc.name || name;
        } catch (_) { /* fall back to filename */ }
        setDoc({ id: data.existing_document_id, name, status: data.existing_status });
      } else if (res.ok) {
        setDoc({ id: data.id, name: data.name || file.name, status: data.status || 'pending' });
      } else {
        throw new Error(data.detail || `Server error: ${res.status}`);
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploadLoading(false);
    }
  }, [portfolioId, authFetch]);

  // Poll while the uploaded/matched document is still ingesting.
  useEffect(() => {
    if (!doc || !portfolioId) return;
    if (doc.status === 'ready' || doc.status === 'error') return;
    const timer = setInterval(() => {
      // no-store: this hits the exact same URL every 2s — without it, the browser's
      // HTTP cache can silently keep serving the first response (progress frozen at
      // whatever it read on the very first poll) instead of a fresh one each tick.
      authFetch(`/portfolios/${portfolioId}/documents`, { cache: 'no-store' })
        .then((r) => r.json())
        .then((data) => {
          const found = (data.documents || []).find((d) => d.id === doc.id);
          if (found) {
            // doc.title (e.g. "US 8,095,879 B2, Neonode Inc., Dec. 10, 2002, User
            // Interface for...") is only written once ingest reaches the metadata step,
            // so it's absent early on — display_name (the compact "Neonode '879" shorthand
            // used elsewhere for space-constrained pickers) and name/original filename are
            // the best available fallbacks until then. NOTE: the SQL alias is
            // "embed_title", but list_portfolio_documents pops it and re-adds it as
            // plain "title" (server.py's `d['title'] = embed_title or ''`) before this
            // JSON ever reaches the client — "embed_title" itself is never present here.
            setDoc((prev) => prev && prev.id === found.id
              ? { ...prev, status: found.status, name: found.title || found.display_name || found.name || prev.name,
                  error_message: found.error_message, progress: found.progress_pct }
              : prev);
          }
        })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [doc, portfolioId, authFetch]);

  // Use an already-ingested document directly (picked from the portfolio browser)
  // instead of uploading — it's already 'ready', so the polling effect above is a no-op
  // for it (skips terminal statuses) and any of usePatentUpload's callers that activate
  // on doc.status === 'ready' fire immediately, same as a checksum-matched reuse.
  const selectExisting = useCallback((existingDoc) => {
    setUploadError(null);
    setDoc({
      id: existingDoc.id,
      name: existingDoc.title || existingDoc.display_name || existingDoc.name,
      status: 'ready',
    });
  }, []);

  const processing = uploadLoading || (doc && (doc.status === 'pending' || doc.status === 'running'));

  return { doc, uploadLoading, uploadError, processing, handleFileSelect, selectExisting };
}

// Popup listing every portfolio the user can see — their own and any public ones — each
// expandable to show its ready documents. Clicking a document selects it directly via
// usePatentUpload's selectExisting, bypassing upload entirely.
// `patentOnly` (default true) restricts the list to documents with is_patent set —
// off only for Claim Chart's "Prior Art" slot, where the reference /priorart compares
// against can be any document type (a patent, but also a journal article, standard,
// or other printed publication — see server.py's `earlier` param, which is not
// patent-specific), unlike the "Patent"/sufficiency slots which must stay patent-only.
function PortfolioBrowser({ portfolios, portfolioDocsMap, onSelect, onClose, patentOnly = true }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  return (
    <div className="suff-info-overlay" onClick={onClose}>
      <div className="portfolio-browser-modal" onClick={(e) => e.stopPropagation()}>
        <button className="suff-info-close" onClick={onClose}>✕</button>
        <h3>Browse Portfolios</h3>
        <div className="portfolio-browser-list">
          {portfolios.length === 0 && <div className="portfolio-browser-empty">No portfolios available.</div>}
          {portfolios.map((p) => {
            const docs = (portfolioDocsMap[p.id] || []).filter((d) => (!patentOnly || d.is_patent) && d.status === 'ready');
            const isOpen = expanded.has(p.id);
            return (
              <div key={p.id} className="portfolio-browser-group">
                <div className="portfolio-browser-row" onClick={() => toggle(p.id)}>
                  <span className={`portfolio-browser-triangle${isOpen ? ' portfolio-browser-triangle-open' : ''}`}>▶</span>
                  <span className="portfolio-browser-name">{p.name}</span>
                  {!p.is_owner && <span className="portfolio-browser-badge">Public</span>}
                  <span className="portfolio-browser-count">{docs.length}</span>
                </div>
                {isOpen && (
                  <div className="portfolio-browser-docs">
                    {docs.length === 0 && <div className="portfolio-browser-empty">{patentOnly ? 'No patents in this portfolio.' : 'No documents in this portfolio.'}</div>}
                    {docs.map((d) => (
                      <div key={d.id} className="portfolio-browser-doc" onClick={() => onSelect(d)}>
                        {d.title || d.display_name || d.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Renders the "Choose File" control + spinner + progress bar + post-processing title,
// backed by a usePatentUpload() instance. `disabled` covers slot-specific busy states
// (e.g. an analysis already running) on top of the upload's own processing state.
function PatentUploadPicker({ label, upload, disabled, portfolios, portfolioDocsMap, patentOnly = true }) {
  const { doc, uploadLoading, uploadError, processing, handleFileSelect, selectExisting } = upload;
  const [showBrowser, setShowBrowser] = useState(false);
  const fileInputRef = useRef(null);
  return (
    <div className="priorart-index-row" style={{ alignItems: 'flex-start', marginBottom: 24 }}>
      <label className="priorart-label">{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            disabled={processing || disabled}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              e.target.value = '';
              if (f) handleFileSelect(f);
            }}
          />
          <button
            type="button"
            className="portfolio-browse-btn"
            disabled={processing || disabled}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            Choose File
          </button>
          <button
            type="button"
            className="portfolio-browse-btn"
            disabled={processing || disabled}
            onClick={() => setShowBrowser(true)}
          >
            Browse Portfolios
          </button>
          {processing && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#888' }}>
              <span className="suff-inline-spinner" />
              {uploadLoading ? 'Uploading…' : `Processing ${doc.name}…`}
            </span>
          )}
        </div>
        {processing && !uploadLoading && (
          <div className="suff-progress-wrap" style={{ margin: 0, maxWidth: 320 }}>
            <div className="suff-progress-track">
              {!doc.progress
                ? <div className="suff-progress-scan" />
                : <div className="suff-progress-fill" style={{ width: `${doc.progress}%` }} />}
            </div>
            <span className="suff-progress-label">{doc.progress ? `${doc.progress}%` : ''}</span>
          </div>
        )}
        {doc && !processing && (
          <span style={{ fontSize: 14, fontWeight: 600, color: doc.status === 'error' ? '#c0392b' : '#333' }}>
            {doc.status === 'ready' && doc.name}
            {doc.status === 'error' && `Failed to process ${doc.name}${doc.error_message ? `: ${doc.error_message}` : ''}`}
          </span>
        )}
      </div>
      {uploadError && <div className="error" style={{ margin: '4px 0 0' }}>{uploadError}</div>}
      {showBrowser && (
        <PortfolioBrowser
          portfolios={portfolios}
          portfolioDocsMap={portfolioDocsMap}
          onClose={() => setShowBrowser(false)}
          onSelect={(d) => { selectExisting(d); setShowBrowser(false); }}
          patentOnly={patentOnly}
        />
      )}
    </div>
  );
}

// One "Prior Art" reference slot for Claim Chart's multi-document earlier param. Owns its
// own usePatentUpload() instance (unmodified — same hook single-select pickers use) and
// reports its resolved ready-document id up to the parent via onDocChange. Kept as its own
// component (rather than calling usePatentUpload in a loop in the parent) because React's
// rules of hooks don't allow a variable number of hook calls in one render.
function PriorArtSlot({ slotId, label, portfolioId, authFetch, disabled, portfolios, portfolioDocsMap, onDocChange, onRemove, removable }) {
  const upload = usePatentUpload(portfolioId, authFetch);
  useEffect(() => {
    onDocChange(slotId, upload.doc && upload.doc.status === 'ready' ? upload.doc.id : null);
  }, [upload.doc, slotId, onDocChange]);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
      <div style={{ flex: 1 }}>
        <PatentUploadPicker label={label} upload={upload} disabled={disabled} portfolios={portfolios} portfolioDocsMap={portfolioDocsMap} patentOnly={false} />
      </div>
      {removable && (
        <button
          type="button"
          className="priorart-slot-remove"
          onClick={() => onRemove(slotId)}
          disabled={disabled}
          title="Remove this reference"
          style={{ marginTop: 6 }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

// Module-level so a figure fetched once (a single patent drawing is commonly
// cited by references across many claims in one claim chart) is never
// re-fetched or re-decoded for the rest of the browser tab's session.
const figureImageCache = new Map(); // s3 key -> blob object URL

function FigureImage({ figKey, label, authFetch, onClick }) {
  const [src, setSrc] = useState(() => figureImageCache.get(figKey) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (figureImageCache.has(figKey)) {
      setSrc(figureImageCache.get(figKey));
      return;
    }
    let cancelled = false;
    authFetch(`/figure_image?key=${encodeURIComponent(figKey)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load figure: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        figureImageCache.set(figKey, url);
        setSrc(url);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [figKey, authFetch]);

  // Fail silently -- the text citation above this already stands on its own,
  // so a missing figure crop shouldn't disrupt the rest of the claim chart.
  if (failed) return null;
  if (!src) return <div className="claim-chart-figure-loading">Loading figure…</div>;
  return (
    <img
      className={`claim-chart-figure-img${onClick ? ' citation-link' : ''}`}
      src={src}
      alt={label}
      title={onClick ? `${label} — click to view in source` : label}
      onClick={onClick}
    />
  );
}

// Distinct palette for claim-term highlighting, cycled by group index so a
// matched concept keeps the same color across the limitation cell and every
// reference cell it appears in (see lim.term_highlights, computed server-side
// by _extract_term_highlights).
const TERM_HIGHLIGHT_COLORS = [
  '#ffe08a', '#a8d8ff', '#b3f0a8', '#ffb3ba',
  '#d5b3ff', '#ffcc99', '#a8fff0', '#ffb3e6',
];

// Same deficient/concern/adequate palette as the .score-*/.verdict-* CSS classes
// (see scoreTier), as plain hex for the DOCX export -- Word runs take a hex
// text color directly, there's no CSS class to reuse there.
const TIER_COLOR_HEX = { deficient: '721C24', concern: '856404', adequate: '155724' };

// Marker for a term that only appears in our own generated argument/summary
// text, never in the verbatim prior-art excerpt. Spans carrying this "color"
// are drawn as a gray UNDERLINE, not a background fill (see renderHighlighted/
// highlightedHtml/the DOCX export's highlightRuns) -- a gray fill read as just
// one more highlight color next to the real palette, when the whole point is
// "our commentary echoes this term," not "this is a literal match to the prior
// art," which the colored fills are reserved for. Still also the marker
// computeHighlightSpans uses to let a real (colored) match win an overlap.
const SUMMARY_HIGHLIGHT_COLOR = '#888888';

// Inline style for a SUMMARY_HIGHLIGHT_COLOR span (shared by the live table
// and the PDF export so the two can't drift).
const SUMMARY_UNDERLINE_CSS = `text-decoration:underline;text-decoration-color:${SUMMARY_HIGHLIGHT_COLOR};text-decoration-thickness:1.5px;text-underline-offset:3px;`;

// True if any of a term_highlights group's matches point at an actual
// numbered reference excerpt (as opposed to only the "summary" pseudo-source
// -- see _extract_term_highlights in server.py). Used to decide whether a
// group gets a real palette color (something was actually found in the
// prior art) or the gray underline (only echoed in our own argument text).
function groupHasReferenceMatch(g) {
  return (g.matches || []).some((m) => m.source !== 'summary');
}

// Given raw `text` and a limitation's term_highlights groups, compute the
// {start, end, color} spans to highlight in it. `pickPhrase(group)` extracts
// the phrase relevant to THIS specific text from one group -- e.g. the
// limitation_phrase itself for the limitation cell, the matching phrase for
// one particular reference's `source` number for that reference's text cell,
// or the summary's own matching phrase for the summary cell. `colorFor(g,
// gi)`, if given, overrides the default palette-cycling color for a group
// (see groupHasReferenceMatch/SUMMARY_HIGHLIGHT_COLOR) -- omit it to always
// cycle the palette, which is what reference cells want (every span there is
// already guaranteed to be a real reference match). Shared by both the live
// table and the PDF export so the two never render differently for the same
// data.
function computeHighlightSpans(text, groups, pickPhrase, colorFor) {
  if (!text || !groups || groups.length === 0) return [];
  const spans = [];
  groups.forEach((g, gi) => {
    const phrase = pickPhrase(g);
    if (!phrase) return;
    const idx = text.indexOf(phrase);
    if (idx === -1) return; // server already verified the phrase is a real substring, but stay defensive
    const color = colorFor ? colorFor(g, gi) : TERM_HIGHLIGHT_COLORS[gi % TERM_HIGHLIGHT_COLORS.length];
    spans.push({ start: idx, end: idx + phrase.length, color, priority: color === SUMMARY_HIGHLIGHT_COLOR ? 0 : 1 });
  });
  // Drop overlaps so highlighting never nests/breaks -- but resolve them by
  // priority first (a colorful, reference-matched span wins over a gray,
  // summary-only one covering the same words) and only fall back to
  // earlier-start-wins among spans of equal priority. Without this, a real
  // match to the prior art could be silently swallowed by our own gray
  // paraphrase highlight landing on the same text first.
  spans.sort((a, b) => (b.priority - a.priority) || (a.start - b.start));
  const clean = [];
  for (const s of spans) {
    if (!clean.some((c) => s.start < c.end && c.start < s.end)) {
      clean.push(s);
    }
  }
  clean.sort((a, b) => a.start - b.start);
  return clean;
}

// Maps a claim-chart limitation's 1-10 argument-strength score to a
// deficient/concern/adequate tier, matching the same tiering sufficiency
// checking uses for its own status badges (see _analyze_claim's "score" field
// in server.py). Returns just the tier name -- callers prepend their own
// prefix (live table reuses sufficiency's "suff-" classes directly; the PDF
// export builds a fully standalone document with its own "score-" classes).
function scoreTier(score) {
  if (score == null) return '';
  if (score <= 3) return 'deficient';
  if (score <= 6) return 'concern';
  return 'adequate';
}

// Argument/rebuttal column headers for the claim chart, plus the lead-in
// sentence shown directly above the chart. Invalidity and Validity get a 4th
// column with the opposing party's rebuttal (lim.rebuttal, a separate
// server-side call that answers the argument column); Neutral has no opposing
// party, so it keeps a single analysis column (rebuttal: null) and no lead-in.
function claimChartColumns(useCase) {
  if (useCase === 'defense') {
    return {
      argument: 'Invalidity Argument', rebuttal: 'Patent Owner Rebuttal',
      leadIn: 'The prior art discloses all limitations of the claim, for at least the following reasons.',
    };
  }
  if (useCase === 'plaintiff') {
    return {
      argument: 'Validity Argument', rebuttal: 'Challenger Rebuttal',
      leadIn: 'The prior art does not disclose all limitations of the claim, for at least the following reasons.',
    };
  }
  return { argument: 'Prior Art Analysis', rebuttal: null, leadIn: null };
}

// What the rebuttal cell says for a row with no rebuttal of its own: a pointer
// row (a dependent claim's preamble, or the synthetic "[Limitations inherited
// from claim N]" row) points at the parent claim, an organizational score-null
// row points at its sub-elements below, and a scored row whose rebuttal
// failed shows a dash rather than looking deliberately empty.
function rebuttalPlaceholder(lim) {
  const parent = lim.see_claim != null
    ? lim.see_claim
    : (lim._inherited ? ((lim.limitation || '').match(/claim (\d+)/) || [])[1] : null);
  if (parent != null) return `See claim ${parent}.`;
  if (lim.score == null) return 'See below';
  return '—';
}

// Every prior-art citation on a claim-chart row, argument and rebuttal alike
// (for collecting figure images and reference names in the exports).
// Specification citations are the challenged patent itself, never prior art.
function priorArtRefs(lim) {
  return [...(lim.references || []), ...((lim.rebuttal && lim.rebuttal.references) || [])];
}

// Notice shown at the top of every claim chart -- the live table, PDF and Word
// export alike. One version for every perspective: the perspective doesn't tell
// us the context (a Neutral chart may be for prosecution or litigation, an
// Invalidity chart for district court or an IPR), so the litigation and
// prosecution guidance both always appear.
const CLAIM_CHART_DISCLAIMER = {
  title: 'Attorney use only',
  sections: [
    {
      heading: 'Verify before use',
      paragraphs: [
        'Review and verify all Gavil outputs before including them in any case or prosecution materials, such as contentions, interrogatory responses, Office Action responses, Information Disclosure Statements, or declarations.',
      ],
    },
    {
      heading: 'In litigation and PTAB proceedings: do not disclose to testifying experts',
      paragraphs: [
        'Treat Gavil as you would a consulting expert. Its claim charts, scores, and analysis may constitute attorney work product if prepared at the direction of counsel in anticipation of litigation or for trial.',
        'Do not disclose Gavil’s outputs or your use of Gavil to a testifying expert or declarant. Materials a testifying expert considers are generally discoverable, so those outputs, the inputs behind them, and even the tool itself could become subject to discovery.',
      ],
    },
    {
      heading: 'In patent prosecution: assume discoverability, and mind your duty of disclosure',
      paragraphs: [
        'Work-product protection often does not extend to patent prosecution. Assume Gavil’s outputs, and the inputs behind them, could be discoverable in later litigation involving the patent.',
        'Prior art that Gavil identifies, and its analysis of that art, may be material to patentability. Individuals substantively involved in prosecution must disclose known material information to the USPTO (37 C.F.R. § 1.56), and reviewing Gavil’s results can create that knowledge.',
      ],
    },
  ],
  footer: 'This notice is not legal advice. Discovery, privilege, and disclosure rules vary.',
};

// A verbatim quote spanning several originally-separate short paragraphs (e.g.
// individual flowchart-box labels, each ingested as its own one-line paragraph
// -- see the ingest pipeline's per-page paragraph chunking) carries the double
// newlines that joined them in the source chunk text, copied through faithfully
// by the model's own "verbatim" quoting. .ref-text's white-space: pre-wrap
// renders every blank line as real vertical space, which reads fine for a
// couple of paragraphs but sprawls badly for a quote stitched from many one-line
// fragments. Collapsing runs of blank lines down to single line breaks keeps
// each fragment on its own line (still readable as distinct pieces) without the
// wasted gap between every single one -- confirmed live as the actual cause of
// a citation rendering far taller than its content warranted.
function compactQuoteText(text) {
  return (text || '').trim().replace(/\n{2,}/g, '\n');
}

// Picks, for a given term_highlights group, the phrase to highlight in one
// specific reference's text (matched by its 1-based `source` number) -- or
// null if that group has no match in this reference.
function pickRefPhrase(group, refSource) {
  const m = (group.matches || []).find((mm) => mm.source === refSource);
  return m ? m.phrase : null;
}

// Picks, for a given term_highlights group, the phrase to highlight in the
// summary/argument text -- server marks these matches with source: "summary"
// (see _extract_term_highlights) since that text is our own generated
// commentary, not a verbatim excerpt. Always rendered as the SUMMARY_HIGHLIGHT_COLOR
// gray underline rather than a palette fill (see computeHighlightSpans).
function pickSummaryPhrase(group) {
  const m = (group.matches || []).find((mm) => mm.source === 'summary');
  return m ? m.phrase : null;
}

// React renderer: raw text + spans -> array of plain strings and <mark> nodes.
function renderHighlighted(text, spans) {
  if (!spans || spans.length === 0) return text;
  const nodes = [];
  let pos = 0;
  spans.forEach((s, i) => {
    if (s.start > pos) nodes.push(text.slice(pos, s.start));
    nodes.push(s.color === SUMMARY_HIGHLIGHT_COLOR ? (
      <span key={i} style={{ textDecoration: 'underline', textDecorationColor: SUMMARY_HIGHLIGHT_COLOR, textDecorationThickness: '1.5px', textUnderlineOffset: '3px' }}>
        {text.slice(s.start, s.end)}
      </span>
    ) : (
      <mark key={i} style={{ backgroundColor: s.color, padding: '0 1px', borderRadius: 2 }}>
        {text.slice(s.start, s.end)}
      </mark>
    ));
    pos = s.end;
  });
  if (pos < text.length) nodes.push(text.slice(pos));
  return nodes;
}

// HTML-string renderer for the PDF export (see handleExportClaimChartPdf) --
// same span data, rendered as an escaped HTML string instead of React nodes.
function highlightedHtml(text, spans, esc) {
  if (!spans || spans.length === 0) return esc(text);
  let html = '';
  let pos = 0;
  spans.forEach((s) => {
    if (s.start > pos) html += esc(text.slice(pos, s.start));
    html += s.color === SUMMARY_HIGHLIGHT_COLOR
      ? `<span style="${SUMMARY_UNDERLINE_CSS}">${esc(text.slice(s.start, s.end))}</span>`
      : `<mark style="background-color:${s.color};padding:0 1px;border-radius:2px;">${esc(text.slice(s.start, s.end))}</mark>`;
    pos = s.end;
  });
  if (pos < text.length) html += esc(text.slice(pos));
  return html;
}

function App() {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently, getAccessTokenWithPopup, user, error: authError } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  const authFetch = useCallback(async (url, opts = {}) => {
    let token;
    try {
      token = await getAccessTokenSilently();
    } catch (e) {
      // Silent renewal fails (e.g. blocked third-party iframe, revoked grant) with
      // one of these Auth0 error codes — fall back to an interactive popup instead
      // of surfacing the raw SDK error ("Consent required" etc.) to the user.
      if (['login_required', 'consent_required', 'interaction_required'].includes(e.error)) {
        try {
          token = await getAccessTokenWithPopup();
        } catch (popupErr) {
          throw new Error('Your session needs to be renewed — please allow pop-ups for this site and try again, or refresh the page to log in.');
        }
      } else {
        throw e;
      }
    }
    const res = await fetch(url, {
      ...opts,
      headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
    });
    if (res.status === 402) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || 'You have run out of credits');
    }
    return res;
  }, [getAccessTokenSilently, getAccessTokenWithPopup]);

  const showLlmChoice = new URLSearchParams(window.location.search).get('llmchoice') !== null
    && new URLSearchParams(window.location.search).get('llmchoice') !== '0';
  const [tab, setTab] = useState(() => {
    const path = window.location.pathname;
    if (path === '/chat') return 'chat';
    if (path === '/sufficiency') return 'sufficiency';
    return 'qa';
  });
  const [userCredits, setUserCredits] = useState(null);
  const [modelOptions, setModelOptions] = useState([]);
  const [indexList, setIndexList] = useState([]);
  const [indexDisplayNames, setIndexDisplayNames] = useState({});
  const [selectedIndex, setSelectedIndex] = useState('');
  const [selectedModel, setSelectedModel] = useState('claude.claude-sonnet-5'); // overwritten by data.models[0] once /models resolves

  // --- Q&A state ---
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- Chat state ---
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [reasoning, setReasoning] = useState('');

  // --- Discrep state ---
  const [discrepInput, setDiscrepInput] = useState('');
  const [discrepTitle, setDiscrepTitle] = useState('');
  const [discrepItems, setDiscrepItems] = useState([]);
  const [discrepSources, setDiscrepSources] = useState([]);
  const [discrepLoading, setDiscrepLoading] = useState(false);
  const [discrepSubmitted, setDiscrepSubmitted] = useState(false);
  const [discrepError, setDiscrepError] = useState(null);
  const [discrepModel, setDiscrepModel] = useState('claude.claude-sonnet-5');
  const chatEndRef = useRef(null);

  // Default-portfolio upload flow: pick a file, upload (or reuse an existing match by
  // checksum) into the user's own "Default" portfolio, poll until ingest is ready.
  // Shared by Sufficiency Check and Claim Chart's two patent slots (see usePatentUpload).
  const [defaultPortfolioId, setDefaultPortfolioId] = useState('');

  // --- Prior Art (claim chart) state ---
  // "Prior Art" (earlier) is multi-slot — /claimchart accepts a list of references (e.g. an
  // obviousness combination ground). One PriorArtSlot per id in priorArtSlotIds, starting
  // with exactly one (matching the pre-multi-doc UX); "+ Add another reference" appends.
  // Each slot reports its own resolved ready-document id into priorArtSlotDocIds; the actual
  // list sent to the server is derived from that map, not tracked as its own state.
  const [priorArtSlotIds, setPriorArtSlotIds] = useState([0]);
  const [priorArtSlotDocIds, setPriorArtSlotDocIds] = useState({});
  const priorArtIndexList = useMemo(
    () => priorArtSlotIds.map((id) => priorArtSlotDocIds[id]).filter(Boolean),
    [priorArtSlotIds, priorArtSlotDocIds]
  );
  const handlePriorArtSlotDocChange = useCallback((slotId, docId) => {
    setPriorArtSlotDocIds((prev) => ({ ...prev, [slotId]: docId }));
  }, []);
  const addPriorArtSlot = useCallback(() => {
    setPriorArtSlotIds((prev) => [...prev, prev.length ? Math.max(...prev) + 1 : 0]);
  }, []);
  const removePriorArtSlot = useCallback((slotId) => {
    setPriorArtSlotIds((prev) => prev.filter((id) => id !== slotId));
    setPriorArtSlotDocIds((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  }, []);
  const togglePriorArtClaim = useCallback((n) => {
    setPriorArtSelectedClaims((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  }, []);
  const PRIOR_ART_MAX_SLOTS = 10; // mirrors the server-side cap in /claimchart

  const [priorArtIndex2, setPriorArtIndex2] = useState('');
  const [priorArtModel, setPriorArtModel] = useState('claude.claude-sonnet-5'); // gpt.gpt-5.5 confirmed to silently drop ~33% of claims on larger patents (empty completions)
  const [priorArtUseCase, setPriorArtUseCase] = useState('defense');
  const [priorArtResults, setPriorArtResults] = useState([]);
  const [priorArtLoading, setPriorArtLoading] = useState(false);
  const [priorArtError, setPriorArtError] = useState(null);
  const [priorArtTotal, setPriorArtTotal] = useState(0);
  // All claim numbers in the selected "Patent" (later) document, and which of
  // them are toggled on for the next Generate -- defaults to every claim on.
  // A Set so individual toggles are O(1) instead of re-scanning an array.
  const [priorArtClaimNumbers, setPriorArtClaimNumbers] = useState([]);
  const [priorArtSelectedClaims, setPriorArtSelectedClaims] = useState(new Set());
  const priorArtUpload2 = usePatentUpload(defaultPortfolioId, authFetch); // "Patent" (later)

  // --- Sufficiency Check state ---
  const [suffIndex, setSuffIndex] = useState('');
  const [suffUseCase, setSuffUseCase] = useState('defense'); // same role vocabulary as Claim Chart
  const [suffPhosita, setSuffPhosita] = useState('');
  const [suffPhositaLoading, setSuffPhositaLoading] = useState(false);
  const [suffLimitations, setSuffLimitations] = useState([]);
  const [suffClaimLevel, setSuffClaimLevel] = useState({});
  const [suffClaimNumbers, setSuffClaimNumbers] = useState([]);
  const [suffMaxClaim, setSuffMaxClaim] = useState('');
  const [suffLoading, setSuffLoading] = useState(false);
  const [suffTotal, setSuffTotal] = useState(0);
  const [suffError, setSuffError] = useState(null);
  const [suffInfoPopup, setSuffInfoPopup] = useState(null); // 'written_description' | 'enablement' | 'indefiniteness' | null
  const suffUpload = usePatentUpload(defaultPortfolioId, authFetch);
  const suffDoc = suffUpload.doc;
  const suffUploadLoading = suffUpload.uploadLoading;
  const suffUploadError = suffUpload.uploadError;
  const suffProcessing = suffUpload.processing;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // --- Prior Art Web state ---
  const [paWebIndex, setPaWebIndex] = useState('');
  const [paWebFilingDate, setPaWebFilingDate] = useState('');
  const [paWebDateLoading, setPaWebDateLoading] = useState(false);
  const [paWebClaims, setPaWebClaims] = useState([]);
  const [paWebLoading, setPaWebLoading] = useState(false);
  const [paWebError, setPaWebError] = useState(null);

  // --- Portfolio state ---
  const [portfolios, setPortfolios] = useState([]);
  const [canUpload, setCanUpload] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('');
  const [portfolioDocOptions, setPortfolioDocOptions] = useState([]);
  const [portfolioDocsMap, setPortfolioDocsMap] = useState({}); // { portfolioId: [doc, ...] }
  const [patentPortfolioId, setPatentPortfolioId] = useState('');

  // Portfolio management page
  const [mgmtPortfolioId, setMgmtPortfolioId] = useState(null);
  const [mgmtDocuments, setMgmtDocuments] = useState([]);
  const [mgmtLoading, setMgmtLoading] = useState(false);
  const [mgmtError, setMgmtError] = useState(null);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [creatingPortfolio, setCreatingPortfolio] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Admin activity log state
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  // Admin users state
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersError, setAdminUsersError] = useState(null);

  // Fetch user info (credits) on login and whenever returning to the home page.
  useEffect(() => {
    if (!isAuthenticated) return;
    authFetch('/auth/me')
      .then((r) => r.json())
      .then((data) => { setUserCredits(data.credits ?? null); setCanUpload(data.can_upload ?? false); setIsAdmin(data.is_admin ?? false); })
      .catch(() => {});
  }, [isAuthenticated, authFetch, location.pathname]);

  // Fetch available models once authenticated.
  useEffect(() => {
    if (!isAuthenticated) return;
    authFetch('/models')
      .then((res) => res.json())
      .then((data) => {
        setModelOptions(data.models);
        const first = data.models[0]?.value || '';
        setSelectedModel(first);
        setDiscrepModel(first);
        setPriorArtModel(first);
      })
      .catch((err) => console.error('Failed to fetch models:', err));
  }, [isAuthenticated, authFetch]);

  // Fetch available indexes once authenticated.
  useEffect(() => {
    if (!isAuthenticated) return;
    authFetch('/indexes')
      .then((res) => res.json())
      .then((data) => {
        setIndexList(data.indexes);
        setIndexDisplayNames(data.display_names);
        // Default to a non-patent index — the Q&A/Chat/Discrep selectors filter patent indexes
        // out, so defaulting to one causes the controlled select value to match nothing.
        const nonPatent = data.indexes.filter((n) => !n.startsWith('patent') && !/^(US)?\d+$/i.test(n));
        setSelectedIndex(nonPatent.includes(data.default) ? data.default : (nonPatent[0] || ''));
        const patents = data.indexes.filter((n) => n.startsWith('patent'));
        const apple = patents.find((n) => (data.display_names[n] || n).toLowerCase().includes('apple'));
        setPriorArtIndex2(apple || patents[1] || patents[0] || '');
        setPaWebIndex(apple || patents[1] || patents[0] || '');
      })
      .catch((err) => console.error('Failed to fetch indexes:', err));
  }, [isAuthenticated, authFetch]);

  // Fetch portfolios and build patent-picker doc options.
  const fetchPortfolios = useCallback(() => {
    if (!isAuthenticated) return;
    authFetch('/portfolios')
      .then((r) => r.json())
      .then((data) => setPortfolios(data.portfolios || []))
      .catch(() => {});
  }, [isAuthenticated, authFetch]);

  // Auto-select the first portfolio when there is no usable source selected.
  useEffect(() => {
    if (selectedPortfolioId || selectedIndex || portfolios.length === 0) return;
    setSelectedPortfolioId(portfolios[0].id);
  }, [portfolios, selectedPortfolioId, selectedIndex]);

  useEffect(() => { fetchPortfolios(); }, [fetchPortfolios]);

  // Every user has their own "Default" portfolio (auto-created on signup, or lazily
  // here for pre-existing accounts) — Sufficiency Check and Claim Chart's patent-upload
  // slots all upload into it directly.
  useEffect(() => {
    if (!isAuthenticated) return;
    authFetch('/portfolios/default')
      .then((r) => r.json())
      .then((data) => setDefaultPortfolioId(data.id))
      .catch(() => {});
  }, [isAuthenticated, authFetch]);

  useEffect(() => {
    if (!isAuthenticated || portfolios.length === 0) {
      setPortfolioDocOptions([]);
      setPortfolioDocsMap({});
      return;
    }
    Promise.all(
      portfolios.map((p) =>
        authFetch(`/portfolios/${p.id}/documents`)
          .then((r) => r.json())
          .then((data) => ({ id: p.id, name: p.name, docs: (data.documents || []).filter((d) => d.status === 'ready') }))
          .catch(() => ({ id: p.id, name: p.name, docs: [] }))
      )
    ).then((results) => {
      const map = {};
      const opts = [];
      results.forEach(({ id, name, docs }) => {
        map[id] = docs;
        docs.forEach((d) => opts.push({ value: d.id, label: `${name} / ${d.name}` }));
      });
      setPortfolioDocsMap(map);
      setPortfolioDocOptions(opts);
      setPatentPortfolioId((cur) => cur || (results[0]?.id ?? ''));
    });
  }, [isAuthenticated, portfolios, authFetch]);

  // Docs in the portfolio currently selected for patent tabs.
  const patentDocs = patentPortfolioId
    ? (portfolioDocsMap[patentPortfolioId] || []).filter(d => d.is_patent && d.status === 'ready')
    : [];

  const isUUID = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
  const patentBody = (id, extra = {}) =>
    isUUID(id) ? { document_id: id, ...extra } : { index_name: id, ...extra };

  const patentIndexes = useMemo(
    () => indexList.filter((n) =>
      n.startsWith('patent') || /^(US)?\d+$/i.test(n)
    ),
    [indexList]
  );

  // Auto-fetch patent filing date from SerpAPI when index changes
  useEffect(() => {
    if (!paWebIndex) return;
    setPaWebDateLoading(true);
    setPaWebFilingDate('');
    authFetch(isUUID(paWebIndex) ? `/patent_date?document_id=${encodeURIComponent(paWebIndex)}` : `/patent_date?index_name=${encodeURIComponent(paWebIndex)}`)
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((data) => { if (data.filing_date) setPaWebFilingDate(data.filing_date); })
      .catch((err) => console.warn(`Patent date lookup failed: ${err.message}`))
      .finally(() => setPaWebDateLoading(false));
  }, [paWebIndex]);

  // Load claims when Prior Art Web tab is active and index changes
  useEffect(() => {
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (tab !== 'priorart_web' || !paWebIndex) return;
    setPaWebLoading(true);
    setPaWebError(null);
    setPaWebClaims([]);
    authFetch(isUUID(paWebIndex) ? `/claims?document_id=${encodeURIComponent(paWebIndex)}` : `/claims?index_name=${encodeURIComponent(paWebIndex)}`)
      .then((r) => { if (!r.ok) throw new Error(`Server error: ${r.status}`); return r.json(); })
      .then((data) => setPaWebClaims((data.claims || []).map((c) => ({ ...c, status: 'idle', limitations: [] }))))
      .catch((err) => setPaWebError(err.message))
      .finally(() => setPaWebLoading(false));
  }, [tab, paWebIndex]);

  // --- Prior Art Web handlers ---
  const handlePaWebClaimClick = async (claim) => {
    if (claim.status !== 'idle') return;
    setPaWebClaims((prev) => prev.map((c) => c.claim_id === claim.claim_id ? { ...c, status: 'loading' } : c));
    try {
      const res = await authFetch('/priorart_web', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claim_text: claim.claim_text, context: claim.context, filing_date: paWebFilingDate, model: 'gpt.gpt-5.4', ...patentBody(paWebIndex) }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line) continue;
          const msg = JSON.parse(line);
          if (msg.type === 'result') {
            setPaWebClaims((prev) => prev.map((c) =>
              c.claim_id === claim.claim_id ? { ...c, status: 'done', limitations: msg.limitations || [] } : c
            ));
          } else if (msg.type === 'error') {
            setPaWebClaims((prev) => prev.map((c) =>
              c.claim_id === claim.claim_id ? { ...c, status: 'error', error: msg.message } : c
            ));
          }
        }
      }
      // Ensure loading is resolved if no result event arrived
      setPaWebClaims((prev) => prev.map((c) =>
        c.claim_id === claim.claim_id && c.status === 'loading' ? { ...c, status: 'done', limitations: [] } : c
      ));
    } catch (err) {
      setPaWebClaims((prev) => prev.map((c) =>
        c.claim_id === claim.claim_id ? { ...c, status: 'error', error: err.message } : c
      ));
    }
  };

  // Fetch claim numbers when patent index changes
  useEffect(() => {
    if (!suffIndex) { setSuffClaimNumbers([]); setSuffMaxClaim(''); return; }
    authFetch('/sufficiency/claims', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patentBody(suffIndex)),
    })
      .then((res) => res.json())
      .then((data) => {
        const nums = data.claim_numbers || [];
        setSuffClaimNumbers(nums);
        setSuffMaxClaim(nums.length > 0 ? nums[nums.length - 1] : '');
      })
      .catch(() => { setSuffClaimNumbers([]); setSuffMaxClaim(''); });
  }, [suffIndex]);

  // Fetch claim numbers for the Claim Chart toggle row when the "Patent"
  // (later/challenged) selection changes -- reuses the same generic
  // /sufficiency/claims endpoint (it just lists a document's claim numbers,
  // nothing sufficiency-specific about it). Defaults every claim to selected.
  useEffect(() => {
    if (!priorArtIndex2) { setPriorArtClaimNumbers([]); setPriorArtSelectedClaims(new Set()); return; }
    authFetch('/sufficiency/claims', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patentBody(priorArtIndex2)),
    })
      .then((res) => res.json())
      .then((data) => {
        const nums = data.claim_numbers || [];
        setPriorArtClaimNumbers(nums);
        setPriorArtSelectedClaims(new Set(nums));
      })
      .catch(() => { setPriorArtClaimNumbers([]); setPriorArtSelectedClaims(new Set()); });
  }, [priorArtIndex2]);

  // Fetch PHOSITA definition when patent index or role changes — a broader/narrower
  // PHOSITA shifts how much of the § 112 burden falls on the specification, so the
  // definition itself depends on which side's argument is being built.
  useEffect(() => {
    if (!suffIndex) return;
    setSuffPhosita('');
    setSuffPhositaLoading(true);
    authFetch('/sufficiency/phosita', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patentBody(suffIndex, { role: suffUseCase })),
    })
      .then((res) => res.json())
      .then((data) => setSuffPhosita(data.phosita || ''))
      .catch(() => setSuffPhosita(''))
      .finally(() => setSuffPhositaLoading(false));
  }, [suffIndex, suffUseCase]);

  // --- Sufficiency Check: upload/reuse a patent in the user's Default portfolio ---
  // (upload + ingest-status polling itself lives in usePatentUpload, shared with Claim Chart)
  const handleSuffFileSelect = (file) => {
    setSuffIndex('');
    setSuffLimitations([]);
    setSuffClaimLevel({});
    suffUpload.handleFileSelect(file);
  };
  const handleSuffSelectExisting = (d) => {
    setSuffIndex('');
    setSuffLimitations([]);
    setSuffClaimLevel({});
    suffUpload.selectExisting(d);
  };

  // Once ingest completes, activate the document for sufficiency checking — this is what
  // fires the existing claim-numbers/PHOSITA effects above (both gated on `suffIndex`).
  useEffect(() => {
    if (suffDoc && suffDoc.status === 'ready' && suffIndex !== suffDoc.id) {
      setSuffIndex(suffDoc.id);
    }
  }, [suffDoc, suffIndex]);

  // --- Sufficiency PDF export ---
  const handleExportSufficiencyPdf = () => {
    const patentName = suffDoc?.name || 'Patent';
    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    // Replace {N} citation markers with their page/column/line label (e.g. "{5}" ->
    // "[P29C14L54]") so citations are hand-traceable in the printed PDF — there's no
    // click-to-jump on a static page, but a real page/column/line reference is.
    // Returns pre-escaped HTML (plain text segments escaped, label wrapped in a
    // colored span) — callers must NOT esc() the result again, unlike other fields.
    const citesToLabels = (s, sources) => {
      const text = String(s ?? '');
      if (!text) return '';
      const re = /\{(\d+)\}/g;
      let result = '';
      let last = 0;
      let m;
      while ((m = re.exec(text)) !== null) {
        result += esc(text.slice(last, m.index));
        const src = sources ? sources[parseInt(m[1], 10) - 1] : null;
        if (src && src.label) {
          result += `<span class="cite-label">${esc(src.label)}</span>`;
        }
        last = m.index + m[0].length;
      }
      result += esc(text.slice(last));
      return result;
    };

    const badgeClass = (s) => s === 'adequate' ? 'adequate' : s === 'deficient' ? 'deficient' : 'concern';

    // Evidence citation labels shown next to the limitation text (e.g. "[P29C14L54]"),
    // matching the on-screen claim-chart-limitation column — the PDF export previously
    // omitted these entirely.
    const evidenceCitesHtml = (lim) => {
      const labels = (lim.evidence || [])
        .map((ev) => ev.label || (ev.citation ? `[${ev.citation}]` : null))
        .filter(Boolean);
      if (labels.length === 0) return '';
      return ` <span class="limitation-cites">${labels.map((l) => `<span class="cite-label">${esc(l)}</span>`).join(' ')}</span>`;
    };

    const renderPdfCell = (lim) => {
      const status = (lim.status || 'concern').toLowerCase();
      const score = lim.score != null ? `<span class="score">${lim.score}/10</span>` : '';
      const docBadges = lim.doctrines
        ? Object.entries({written_description:'Written Desc.', enablement:'Enablement', indefiniteness:'Indefiniteness'})
            .map(([k, abbr]) => `<span class="badge ${badgeClass(lim.doctrines[k] || 'concern')}" style="font-size:8px;margin-left:3px">${abbr}</span>`).join('')
        : '';
      const issuesHtml = (lim.issues && lim.issues.length > 0)
        ? `<ul class="issues">${lim.issues.map((i) => `<li>${citesToLabels(i, lim.spec_sources)}</li>`).join('')}</ul>` : '';
      let precedentHtml = '';
      if (lim.precedent && (status === 'concern' || status === 'deficient')) {
        const inv = (lim.precedent.for_invalidity || []).map(c => `<li>${esc(c)}</li>`).join('');
        if (status === 'deficient') {
          precedentHtml = `<div class="precedent"><div style="font-weight:700;margin-bottom:3px;">Precedent</div><ul class="issues">${inv}</ul></div>`;
        } else {
          const val = (lim.precedent.for_validity || []).map(c => `<li>${esc(c)}</li>`).join('');
          precedentHtml = `<div class="precedent">
            <div style="font-weight:700;margin-bottom:4px;">Precedent</div>
            <div style="color:#721c24;font-weight:600;font-size:9px;margin-bottom:2px;">Supporting deficiency</div>
            <ul class="issues">${inv}</ul>
            <div style="color:#155724;font-weight:600;font-size:9px;margin-top:5px;margin-bottom:2px;">Supporting adequacy</div>
            <ul class="issues">${val}</ul>
          </div>`;
        }
      }
      return `<div><span class="badge ${badgeClass(status)}">${esc(status)}</span>${score}${docBadges}</div>
              <div class="analysis-text">${citesToLabels(lim.analysis, lim.spec_sources)}${issuesHtml}${precedentHtml}</div>`;
    };

    let rows = '';
    let i = 0;
    while (i < suffLimitations.length) {
      const r = suffLimitations[i];
      let j = i + 1;
      while (j < suffLimitations.length && suffLimitations[j].claim_number === r.claim_number) j++;
      const claimLims = suffLimitations.slice(i, j);
      const claimRowSpan = claimLims.length + (suffClaimLevel[r.claim_number] ? 1 : 0);
      claimLims.forEach((lim, li) => {
        const claimCell = li === 0
          ? `<td class="col-claim" rowspan="${claimRowSpan}">Claim ${lim.claim_number}</td>`
          : '';
        rows += `<tr>
          ${claimCell}
          <td class="col-limitation">${esc(lim.limitation)}${evidenceCitesHtml(lim)}</td>
          <td class="col-analysis">${renderPdfCell(lim)}</td>
        </tr>`;
      });
      const cl = suffClaimLevel[r.claim_number];
      if (cl) {
        const status = (cl.status || 'concern').toLowerCase();
        const score = cl.score != null ? `<span class="score">${cl.score}/10</span>` : '';
        const issues = (cl.issues && cl.issues.length > 0)
          ? `<ul class="issues">${cl.issues.map((iss) => `<li>${citesToLabels(iss, cl.spec_sources)}</li>`).join('')}</ul>` : '';
        rows += `<tr class="claim-level-row">
          <td class="col-limitation claim-level-label">Claim scope vs. embodiments</td>
          <td class="col-analysis">
            <div><span class="badge ${badgeClass(status)}">${esc(status)}</span>${score}</div>
            <div class="analysis-text">${citesToLabels(cl.analysis, cl.spec_sources)}${issues}</div>
          </td>
        </tr>`;
      }
      i = j;
    }

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(patentName)} — Sufficiency Check</title>
  <style>
    @page { size: A4 landscape; margin: 1.5cm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, sans-serif; font-size: 10.5px; color: #222; margin: 0; }
    h1 { font-size: 15px; margin: 0 0 3px; }
    .meta { font-size: 9.5px; color: #666; margin-bottom: 10px; }
    .phosita-box { border: 1px solid #ccc; padding: 8px 10px; margin-bottom: 14px; font-size: 10px; line-height: 1.5; background: #f9f9f9; border-radius: 4px; }
    .phosita-label { font-weight: bold; margin-bottom: 3px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #eef2f7; padding: 6px 8px; font-size: 10px; font-weight: 700; text-align: left; border: 1px solid #bbb; }
    td { padding: 6px 8px; font-size: 10px; line-height: 1.45; vertical-align: top; border: 1px solid #ddd; }
    .col-claim { width: 5%; text-align: center; font-weight: 600; background: #fafafa; }
    .col-limitation { width: 20%; white-space: pre-wrap; word-break: break-word; }
    .limitation-cites { font-size: 8.5px; white-space: nowrap; }
    .cite-label { color: #5b7fa6; font-weight: 600; }
    .col-analysis { width: 75%; word-break: break-word; }
    .badge { display: inline-block; padding: 1px 5px; border-radius: 3px; font-size: 9px; font-weight: 700; text-transform: capitalize; }
    .adequate { background: #d4edda; color: #155724; }
    .concern { background: #fff3cd; color: #7d5c00; }
    .deficient { background: #f8d7da; color: #721c24; }
    .score { font-size: 9px; margin-left: 5px; color: #555; }
    .analysis-text { margin-top: 4px; }
    .issues { margin: 4px 0 0 14px; padding: 0; }
    .issues li { margin-bottom: 2px; }
    .precedent { margin-top: 6px; padding-top: 5px; border-top: 1px solid #e5e5e5; font-size: 9.5px; }
    .claim-level-row td { background: #fafbff; font-style: italic; }
    .claim-level-label { color: #555; }
    tr { page-break-inside: avoid; }
  </style>
</head>
<body>
  <h1>${esc(patentName)} — Sufficiency Check</h1>
  <div class="meta">Generated ${esc(now)}</div>
  ${suffPhosita ? `<div class="phosita-box"><div class="phosita-label">PHOSITA Definition</div>${esc(suffPhosita)}</div>` : ''}
  <table>
    <thead>
      <tr>
        <th class="col-claim">Claim</th>
        <th class="col-limitation">Limitation</th>
        <th class="col-analysis">§ 112 Analysis</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  // One claim-chart citation in the live table. `isSpec` marks a citation to the
  // challenged patent's own specification ([S#], claim-construction support): it
  // gets no term highlighting, since term_highlights' source numbers refer to the
  // prior-art excerpts, which an [S#] number would collide with.
  const renderClaimChartRef = (ref, lim, j, isSpec = false) => {
    const refClickable = ref.filename && ref.bboxes && ref.bboxes.length > 0;
    const refEmphasis = ref.emphasis_bboxes && ref.emphasis_bboxes.length > 0 ? ref.emphasis_bboxes : null;
    return (
      <div key={j} className="claim-chart-ref">
        <div className="claim-chart-ref-title">
          {ref.patent_name} at {ref.location}
          {ref.source != null && (
            <span
              className={refClickable ? 'citation-link claim-chart-cite' : 'claim-chart-cite'}
              onClick={refClickable ? () => handleSourceClick(ref, { showSummary: true, emphasisBboxes: refEmphasis }) : undefined}
            >
              [{isSpec ? 'S' : ''}{ref.source}]
            </span>
          )}
          {(ref.figure_images || []).filter((f) => f.cross_ref).map((fig, fi) => {
            const figClickable = ref.filename && fig.bboxes && fig.bboxes.length > 0;
            return (
              <span
                key={fi}
                className={figClickable ? 'citation-link claim-chart-cite' : 'claim-chart-cite'}
                onClick={figClickable ? () => handleSourceClick(
                  { filename: ref.filename, bboxes: fig.bboxes, title: ref.title },
                  { showSummary: true }
                ) : undefined}
              >
                [{fig.label}]
              </span>
            );
          })}
        </div>
        <div className="claim-chart-ref-text">
          {(() => {
            // See the matching comment in the PDF export's
            // refTextHtml -- compacted for the same reason.
            const text = compactQuoteText(ref.text);
            const alreadyLabeled = /^figure shows:/i.test(text.trim());
            // A "direct" figure_images entry means this citation's
            // OWN text IS the figure's vision-generated caption (see
            // _format_pincite/figure attachment in server.py) -- only
            // then is the "Figure shows:" relabel appropriate. A
            // "cross_ref" entry means the cited text is genuine prose
            // that merely *mentions* a figure (e.g. "Referring to FIG.
            // 1A, ...") -- that text is still a real verbatim quote and
            // must stay quoted as-is; the figure is shown alongside it
            // as supporting evidence, not as what's being quoted.
            const hasDirectFigure = (ref.figure_images || []).some((f) => !f.cross_ref);
            const isFigure = hasDirectFigure || alreadyLabeled;
            const spans = isSpec ? [] : computeHighlightSpans(text, lim.term_highlights, (g) => pickRefPhrase(g, ref.source));
            const highlighted = renderHighlighted(text, spans);
            if (!isFigure) return <>"{highlighted}"</>;
            return alreadyLabeled ? highlighted : <>Figure shows: {highlighted}</>;
          })()}
        </div>
        {ref.figure_images && ref.figure_images.length > 0 && (
          <div className="claim-chart-ref-figures">
            {ref.figure_images.map((fig, fi) => (
              <FigureImage
                key={fi}
                figKey={fig.key}
                label={fig.label}
                authFetch={authFetch}
                onClick={
                  ref.filename && fig.bboxes && fig.bboxes.length > 0
                    ? () => handleSourceClick(
                        { filename: ref.filename, bboxes: fig.bboxes, title: ref.title },
                        { showSummary: true }
                      )
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // The argument or rebuttal body of one claim-chart cell: its explanation, the
  // prior-art citations behind it, then any specification citations it relies on
  // for claim construction. `summaryHighlights` is false for the rebuttal, whose
  // text term_highlights never looked at.
  const renderClaimChartBody = (item, lim, summaryHighlights) => {
    const hasSummary = !!(item.summary && item.summary.trim());
    const refs = item.references || [];
    const specRefs = item.spec_references || [];
    return (
      <>
        {hasSummary && (
          <div className="claim-chart-summary">
            {summaryHighlights
              ? renderHighlighted(item.summary, computeHighlightSpans(item.summary, lim.term_highlights, pickSummaryPhrase, () => SUMMARY_HIGHLIGHT_COLOR))
              : item.summary}
          </div>
        )}
        {refs.length > 0 && (
          <>
            {hasSummary && <div className="claim-chart-see">See, e.g:</div>}
            {refs.map((ref, j) => renderClaimChartRef(ref, lim, j))}
          </>
        )}
        {specRefs.length > 0 && (
          <div className="claim-chart-spec-refs">
            <div className="claim-chart-see">Specification:</div>
            {specRefs.map((ref, j) => renderClaimChartRef(ref, lim, j, true))}
          </div>
        )}
      </>
    );
  };

  // --- Claim Chart HTML export ---
  const handleExportClaimChartPdf = async () => {
    // Must open synchronously, before any await -- browsers only allow
    // window.open() to bypass the popup blocker when it's a direct result of
    // the user gesture that triggered this handler. Fetching figure images
    // first and opening the window afterward (as a first pass here did) loses
    // that "trusted" window by the time window.open() runs, and the browser
    // silently returns null instead of a window, confirmed live via a real
    // "Cannot read properties of null (reading 'document')" console error.
    const w = window.open('', '_blank');
    if (w) {
      w.document.write('<!DOCTYPE html><html><body style="font-family:sans-serif;padding:2em;color:#666;">Generating export…</body></html>');
    }

    const patentName = priorArtUpload2.doc?.name || 'Patent';
    const useCaseLabel = { plaintiff: 'Validity', defense: 'Invalidity', examiner: 'Neutral' }[priorArtUseCase] || priorArtUseCase;
    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    // Same figure-vs-verbatim-quote logic as the on-screen table (see
    // claim-chart-ref-text above) -- duplicated rather than shared, matching
    // how handleExportSufficiencyPdf already duplicates its own render logic
    // instead of factoring out a shared helper. Term highlighting itself
    // (computeHighlightSpans/highlightedHtml) IS shared with the live table,
    // so highlight colors/placement can't drift between screen and PDF.
    const refTextHtml = (ref, lim, isSpec = false) => {
      // .ref-text uses white-space: pre-wrap (to preserve genuine mid-quote
      // line breaks in a multi-paragraph excerpt), which also faithfully
      // renders any stray leading/trailing whitespace (a visible left-indent,
      // confirmed live) and every blank line in a many-fragment quote (a
      // sprawling, mostly-empty citation, also confirmed live) -- see
      // compactQuoteText.
      const text = compactQuoteText(ref.text);
      const alreadyLabeled = /^figure shows:/i.test(text.trim());
      // See the matching comment at the live-table call site -- only a "direct"
      // figure_images entry (the citation's own text IS the figure's caption)
      // justifies the "Figure shows:" relabel; a "cross_ref" entry means the
      // quoted text is genuine prose that just mentions the figure.
      const hasDirectFigure = (ref.figure_images || []).some((f) => !f.cross_ref);
      const isFigure = hasDirectFigure || alreadyLabeled;
      // No term highlights on a specification ([S#]) citation -- see renderClaimChartRef.
      const spans = isSpec ? [] : computeHighlightSpans(text, lim.term_highlights, (g) => pickRefPhrase(g, ref.source));
      const highlighted = highlightedHtml(text, spans, esc);
      if (!isFigure) return `"${highlighted}"`;
      return alreadyLabeled ? highlighted : `Figure shows: ${highlighted}`;
    };

    // Prior art reference names, derived from the results themselves (not the
    // current picker selection, which could have changed since this chart was
    // generated) -- each reference already carries its own patent_name.
    const refNames = Array.from(new Set(
      priorArtResults.flatMap((r) => r.limitations.flatMap((lim) => priorArtRefs(lim).map((ref) => ref.patent_name).filter(Boolean)))
    ));

    // Fetch every cited figure image up front and inline it as a data URI --
    // the print window is a fresh, unauthenticated document, so a plain
    // <img src="/figure_image?..."> would 401 there even though this page's
    // own fetch is authenticated.
    const figureKeys = Array.from(new Set(
      priorArtResults.flatMap((r) => r.limitations.flatMap((lim) => priorArtRefs(lim).flatMap((ref) => (ref.figure_images || []).map((f) => f.key))))
    ));
    const figureDataUrls = {};
    await Promise.all(figureKeys.map(async (key) => {
      try {
        const res = await authFetch(`/figure_image?key=${encodeURIComponent(key)}`);
        if (!res.ok) return;
        const blob = await res.blob();
        figureDataUrls[key] = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        // Best-effort -- a missing figure image shouldn't block the export.
      }
    }));

    const refHtml = (ref, lim, isSpec = false) => {
      const figuresHtml = (ref.figure_images || [])
        .map((fig) => figureDataUrls[fig.key]
          ? `<img class="ref-figure" src="${figureDataUrls[fig.key]}" alt="${esc(fig.label)}" title="${esc(fig.label)}" />`
          : '')
        .join('');
      return `<div class="ref">
        <div class="ref-title">${esc(ref.patent_name)} at ${esc(ref.location)}${ref.source != null ? ` <span class="cite-label">[${isSpec ? 'S' : ''}${esc(ref.source)}]</span>` : ''}</div>
        <div class="ref-text">${refTextHtml(ref, lim, isSpec)}</div>
        ${figuresHtml ? `<div class="ref-figures">${figuresHtml}</div>` : ''}
      </div>`;
    };

    // The argument or rebuttal body of one cell -- same structure as the live
    // table's renderClaimChartBody.
    const bodyHtml = (item, lim, summaryHighlights) => {
      const hasSummary = !!(item.summary && item.summary.trim());
      const refs = item.references || [];
      const specRefs = item.spec_references || [];
      const summaryHtml = hasSummary
        ? `<div class="summary">${summaryHighlights
          ? highlightedHtml(item.summary, computeHighlightSpans(item.summary, lim.term_highlights, pickSummaryPhrase, () => SUMMARY_HIGHLIGHT_COLOR), esc)
          : esc(item.summary)}</div>`
        : '';
      const refsHtml = refs.length > 0
        ? `${hasSummary ? '<div class="see">See, e.g:</div>' : ''}${refs.map((ref) => refHtml(ref, lim)).join('')}`
        : '';
      const specHtml = specRefs.length > 0
        ? `<div class="spec-refs"><div class="see">Specification:</div>${specRefs.map((ref) => refHtml(ref, lim, true)).join('')}</div>`
        : '';
      return `${summaryHtml}${refsHtml}${specHtml}`;
    };

    const columns = claimChartColumns(priorArtUseCase);
    let rows = '';
    priorArtResults.forEach((r) => {
      const lims = r.limitations.length > 0 ? r.limitations : [{ limitation: '', summary: '', references: [] }];
      lims.forEach((lim, li) => {
        const verdictHtml = r.verdict
          ? `<div class="verdict-badge verdict-${r.verdict.tier}">${esc(r.verdict.label)}</div>`
          : '';
        const claimCell = li === 0
          ? `<td class="col-claim" rowspan="${lims.length}">Claim ${esc(r.claim_number)}${verdictHtml}</td>`
          : '';
        const hasSummary = !!(lim.summary && lim.summary.trim());
        const limHtml = highlightedHtml(lim.limitation, computeHighlightSpans(
          lim.limitation, lim.term_highlights, (g) => g.limitation_phrase,
          (g, gi) => groupHasReferenceMatch(g) ? TERM_HIGHLIGHT_COLORS[gi % TERM_HIGHLIGHT_COLORS.length] : SUMMARY_HIGHLIGHT_COLOR
        ), esc);
        const scoreHtml = lim.score != null
          ? `<div><span class="score-badge score-${scoreTier(lim.score)}">${lim.score}/10</span></div>`
          : '';
        const seeBelowHtml = lim.score == null && !lim._inherited && !hasSummary ? '<div class="see">See below</div>' : '';
        const rebuttalCell = columns.rebuttal
          ? `<td class="col-analysis col-rebuttal">${lim.rebuttal
            ? bodyHtml(lim.rebuttal, lim, false)
            : `<div class="see">${esc(rebuttalPlaceholder(lim))}</div>`}</td>`
          : '';
        rows += `<tr>
          ${claimCell}
          <td class="col-limitation">${scoreHtml}${limHtml}</td>
          <td class="col-analysis">${seeBelowHtml}${bodyHtml(lim, lim, true)}</td>
          ${rebuttalCell}
        </tr>`;
      });
    });

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(patentName)} — ${esc(useCaseLabel)} Claim Chart</title>
  <style>
    @page { size: A4 landscape; margin: 1.5cm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, sans-serif; font-size: 10.5px; color: #222; margin: 0; }
    h1 { font-size: 15px; margin: 0 0 3px; }
    .meta { font-size: 9.5px; color: #666; margin-bottom: 10px; }
    .refs-box { border: 1px solid #ccc; padding: 8px 10px; margin-bottom: 14px; font-size: 10px; line-height: 1.5; background: #f9f9f9; border-radius: 4px; }
    .disclaimer { border: 1px solid #c9a227; border-left-width: 4px; padding: 8px 12px; margin-bottom: 14px; font-size: 9.5px; line-height: 1.45; background: #fffbea; border-radius: 4px; page-break-inside: avoid; }
    .disclaimer-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
    .disclaimer-heading { font-weight: 700; margin-top: 6px; }
    .disclaimer p { margin: 2px 0 0; }
    .disclaimer-footer { font-style: italic; color: #555; margin-top: 6px; }
    .lead-in { font-size: 11px; margin: 0 0 8px; }
    .refs-label { font-weight: bold; margin-bottom: 3px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #eef2f7; padding: 6px 8px; font-size: 10px; font-weight: 700; text-align: left; border: 1px solid #bbb; }
    td { padding: 6px 8px; font-size: 10px; line-height: 1.45; vertical-align: top; border: 1px solid #ddd; }
    .col-claim { width: 6%; text-align: center; font-weight: 600; background: #fafafa; }
    .col-limitation { width: ${columns.rebuttal ? 22 : 24}%; white-space: pre-wrap; word-break: break-word; }
    .col-analysis { width: ${columns.rebuttal ? 36 : 70}%; word-break: break-word; }
    .col-rebuttal { background: #fbfbfd; }
    .spec-refs { margin-top: 8px; padding-top: 5px; border-top: 1px dashed #ccc; }
    .score-badge { display: inline-block; font-size: 9.5px; font-weight: 700; padding: 2px 6px; border-radius: 4px; margin-bottom: 6px; background: #fff; }
    .score-deficient { border: 1px solid #721c24; color: #721c24; }
    .score-concern { border: 1px solid #856404; color: #856404; }
    .score-adequate { border: 1px solid #155724; color: #155724; }
    .verdict-badge { display: inline-block; background: #fff; font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 4px; margin-top: 6px; text-transform: uppercase; letter-spacing: 0.3px; }
    .verdict-deficient { border: 1px solid #721c24; color: #721c24; }
    .verdict-concern { border: 1px solid #856404; color: #856404; }
    .verdict-adequate { border: 1px solid #155724; color: #155724; }
    .summary { margin-bottom: 6px; }
    .see { font-style: italic; color: #666; margin-bottom: 4px; }
    .ref { margin-bottom: 10px; }
    .ref:last-child { margin-bottom: 0; }
    .ref-title { font-weight: 700; }
    .cite-label { color: #5b7fa6; font-weight: 600; }
    .ref-text { white-space: pre-wrap; margin-top: 2px; }
    .ref-figures { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 5px; }
    .ref-figure { max-width: 160px; max-height: 160px; border: 1px solid #ddd; border-radius: 3px; background: #fff; padding: 2px; }
    tr { page-break-inside: avoid; }
  </style>
</head>
<body>
  <h1>${esc(patentName)} — ${esc(useCaseLabel)} Claim Chart</h1>
  <div class="meta">Generated ${esc(now)} &middot; Perspective: ${esc(useCaseLabel)}</div>
  <div class="disclaimer">
    <div class="disclaimer-title">${esc(CLAIM_CHART_DISCLAIMER.title)}</div>
    ${CLAIM_CHART_DISCLAIMER.sections.map((sec) => `<div class="disclaimer-heading">${esc(sec.heading)}</div>${sec.paragraphs.map((para) => `<p>${esc(para)}</p>`).join('')}`).join('')}
    <div class="disclaimer-footer">${esc(CLAIM_CHART_DISCLAIMER.footer)}</div>
  </div>
  ${refNames.length > 0 ? `<div class="refs-box"><div class="refs-label">Prior Art References</div>${refNames.map(esc).join('<br/>')}</div>` : ''}
  ${columns.leadIn ? `<p class="lead-in">${esc(columns.leadIn)}</p>` : ''}
  <table>
    <thead>
      <tr>
        <th class="col-claim">Claim</th>
        <th class="col-limitation">Limitation</th>
        <th class="col-analysis">${esc(columns.argument)}</th>
        ${columns.rebuttal ? `<th class="col-analysis">${esc(columns.rebuttal)}</th>` : ''}
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

    if (!w) return; // popup blocked -- nothing more we can do
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Wait for every figure <img> to actually finish loading/decoding before
    // printing -- even though the src is an already-in-memory data URI (no
    // network round trip), the browser still needs a paint/decode cycle, and
    // calling print() immediately after document.close() can race that,
    // producing a printed page with blank figures despite the live page
    // looking correct.
    const imgs = Array.from(w.document.images);
    await Promise.all(imgs.map((img) => img.complete
      ? Promise.resolve()
      : new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })));
    w.print();
  };

  // --- Claim Chart native .docx export ---
  // A real Word document generated directly, not a PDF round-tripped through
  // Word's own "open PDF as Word doc" importer -- that importer reconstructs
  // paragraph/run structure and background-color positioning from a flattened
  // PDF content stream using its own heuristics, and confirmed live to visibly
  // misplace our highlight colors relative to their text when Word reflows
  // them. Generating .docx directly maps our highlights/tables/images to real
  // Word formatting (run shading, table cells, inline images) instead.
  //
  // Deliberately duplicates handleExportClaimChartPdf's content logic rather
  // than sharing it -- the two render to fundamentally different target APIs
  // (an HTML string vs. docx's object tree), so a shared helper would need to
  // abstract over both anyway. computeHighlightSpans (the actual span
  // *computation*, as opposed to how a span becomes marked-up output) is
  // still shared, so highlight placement can't drift between the two exports.
  const handleExportClaimChartDocx = async () => {
    const patentName = priorArtUpload2.doc?.name || 'Patent';
    const useCaseLabel = { plaintiff: 'Validity', defense: 'Invalidity', examiner: 'Neutral' }[priorArtUseCase] || priorArtUseCase;
    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const shadeHex = (color) => (color || '').replace('#', '').toUpperCase();

    // Same span-walk as highlightedHtml/renderHighlighted, emitting DocxTextRuns
    // (with a shading fill on the highlighted slices) instead of marked-up HTML
    // or React nodes -- the third renderer for the one shared span computation.
    const highlightRuns = (text, spans, extra = {}) => {
      if (!spans || spans.length === 0) return [new DocxTextRun({ text, ...extra })];
      const runs = [];
      let pos = 0;
      spans.forEach((s) => {
        if (s.start > pos) runs.push(new DocxTextRun({ text: text.slice(pos, s.start), ...extra }));
        runs.push(new DocxTextRun({
          text: text.slice(s.start, s.end),
          ...(s.color === SUMMARY_HIGHLIGHT_COLOR
            ? { underline: { type: 'single', color: shadeHex(SUMMARY_HIGHLIGHT_COLOR) } }
            : { shading: { fill: shadeHex(s.color) } }),
          ...extra,
        }));
        pos = s.end;
      });
      if (pos < text.length) runs.push(new DocxTextRun({ text: text.slice(pos), ...extra }));
      return runs;
    };

    const refNames = Array.from(new Set(
      priorArtResults.flatMap((r) => r.limitations.flatMap((lim) => priorArtRefs(lim).map((ref) => ref.patent_name).filter(Boolean)))
    ));

    // Fetch every cited figure image up front -- both its bytes (for ImageRun)
    // and its natural pixel size (transformation.width/height is required and
    // has no "auto" option, so the aspect ratio has to be computed here rather
    // than left to layout the way CSS max-width/max-height does for the PDF/
    // live-table versions).
    const MAX_FIG_W = 260, MAX_FIG_H = 220;
    const figureKeys = Array.from(new Set(
      priorArtResults.flatMap((r) => r.limitations.flatMap((lim) => priorArtRefs(lim).flatMap((ref) => (ref.figure_images || []).map((f) => f.key))))
    ));
    const figureData = {};
    await Promise.all(figureKeys.map(async (key) => {
      let objectUrl;
      try {
        const res = await authFetch(`/figure_image?key=${encodeURIComponent(key)}`);
        if (!res.ok) return;
        const blob = await res.blob();
        const buffer = await blob.arrayBuffer();
        objectUrl = URL.createObjectURL(blob);
        const { width, height } = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = reject;
          img.src = objectUrl;
        });
        const ratio = Math.min(MAX_FIG_W / width, MAX_FIG_H / height, 1);
        figureData[key] = { buffer, width: Math.round(width * ratio), height: Math.round(height * ratio) };
      } catch {
        // Best-effort -- a missing figure image shouldn't block the export.
      } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }
    }));

    const buildRefParagraphs = (ref, lim, isSpec = false) => {
      const paras = [];
      const titleText = `${ref.patent_name} at ${ref.location}${ref.source != null ? ` [${isSpec ? 'S' : ''}${ref.source}]` : ''}`;
      paras.push(new DocxParagraph({ children: [new DocxTextRun({ text: titleText, bold: true })], spacing: { after: 40 } }));

      // Same figure-vs-verbatim-quote logic as refTextHtml (see
      // handleExportClaimChartPdf) -- duplicated for the same reason the two
      // export functions are duplicated overall (see the top-level comment).
      const text = compactQuoteText(ref.text);
      const alreadyLabeled = /^figure shows:/i.test(text);
      const hasDirectFigure = (ref.figure_images || []).some((f) => !f.cross_ref);
      const isFigure = hasDirectFigure || alreadyLabeled;
      // No term highlights on a specification ([S#]) citation -- see renderClaimChartRef.
      const spans = isSpec ? [] : computeHighlightSpans(text, lim.term_highlights, (g) => pickRefPhrase(g, ref.source));
      let refRuns;
      if (!isFigure) {
        refRuns = [new DocxTextRun({ text: '"' }), ...highlightRuns(text, spans), new DocxTextRun({ text: '"' })];
      } else if (alreadyLabeled) {
        refRuns = highlightRuns(text, spans);
      } else {
        refRuns = [new DocxTextRun({ text: 'Figure shows: ' }), ...highlightRuns(text, spans)];
      }
      paras.push(new DocxParagraph({ children: refRuns, spacing: { after: 80 } }));

      (ref.figure_images || []).forEach((fig) => {
        const data = figureData[fig.key];
        if (!data) return;
        paras.push(new DocxParagraph({
          children: [new DocxImageRun({ type: 'jpg', data: data.buffer, transformation: { width: data.width, height: data.height } })],
          spacing: { after: 80 },
        }));
      });
      return paras;
    };

    const seeParagraph = (text) => new DocxParagraph({
      children: [new DocxTextRun({ text, italics: true, color: '666666' })],
      spacing: { after: 60 },
    });

    // The argument or rebuttal body of one cell -- same structure as the live
    // table's renderClaimChartBody.
    const buildBodyParagraphs = (item, lim, summaryHighlights) => {
      const paras = [];
      const hasSummary = !!(item.summary && item.summary.trim());
      if (hasSummary) {
        paras.push(new DocxParagraph({
          children: summaryHighlights
            ? highlightRuns(item.summary, computeHighlightSpans(item.summary, lim.term_highlights, pickSummaryPhrase, () => SUMMARY_HIGHLIGHT_COLOR))
            : [new DocxTextRun({ text: item.summary })],
          spacing: { after: 60 },
        }));
      }
      const refs = item.references || [];
      if (refs.length > 0) {
        if (hasSummary) {
          paras.push(new DocxParagraph({
            children: [new DocxTextRun({ text: 'See, e.g:', italics: true, color: '666666' })],
            spacing: { after: 40 },
          }));
        }
        refs.forEach((ref) => paras.push(...buildRefParagraphs(ref, lim)));
      }
      const specRefs = item.spec_references || [];
      if (specRefs.length > 0) {
        paras.push(new DocxParagraph({
          children: [new DocxTextRun({ text: 'Specification:', italics: true, color: '666666' })],
          spacing: { before: 120, after: 40 },
        }));
        specRefs.forEach((ref) => paras.push(...buildRefParagraphs(ref, lim, true)));
      }
      return paras;
    };

    const columns = claimChartColumns(priorArtUseCase);
    const widths = columns.rebuttal
      ? { claim: 6, limitation: 22, argument: 36, rebuttal: 36 }
      : { claim: 6, limitation: 24, argument: 70 };
    const claimRows = [];
    priorArtResults.forEach((r) => {
      const lims = r.limitations.length > 0 ? r.limitations : [{ limitation: '', summary: '', references: [] }];
      lims.forEach((lim, li) => {
        const hasSummary = !!(lim.summary && lim.summary.trim());
        const limParas = [];
        if (lim.score != null) {
          limParas.push(new DocxParagraph({
            children: [new DocxTextRun({ text: `${lim.score}/10`, bold: true, color: TIER_COLOR_HEX[scoreTier(lim.score)] })],
            spacing: { after: 60 },
          }));
        }
        limParas.push(new DocxParagraph({
          children: highlightRuns(lim.limitation, computeHighlightSpans(
            lim.limitation, lim.term_highlights, (g) => g.limitation_phrase,
            (g, gi) => groupHasReferenceMatch(g) ? TERM_HIGHLIGHT_COLORS[gi % TERM_HIGHLIGHT_COLORS.length] : SUMMARY_HIGHLIGHT_COLOR
          )),
        }));

        const analysisParas = [];
        if (lim.score == null && !lim._inherited && !hasSummary) analysisParas.push(seeParagraph('See below'));
        analysisParas.push(...buildBodyParagraphs(lim, lim, true));
        if (analysisParas.length === 0) analysisParas.push(new DocxParagraph({ children: [] }));

        const cells = [];
        if (li === 0) {
          const claimCellChildren = [new DocxParagraph({ children: [new DocxTextRun({ text: `Claim ${r.claim_number}`, bold: true })] })];
          if (r.verdict) {
            claimCellChildren.push(new DocxParagraph({
              children: [new DocxTextRun({ text: r.verdict.label, bold: true, color: TIER_COLOR_HEX[r.verdict.tier] })],
            }));
          }
          cells.push(new DocxTableCell({
            children: claimCellChildren, rowSpan: lims.length, verticalAlign: DocxVerticalAlign.TOP,
            shading: { fill: 'FAFAFA' }, width: { size: widths.claim, type: DocxWidthType.PERCENTAGE },
          }));
        }
        cells.push(new DocxTableCell({ children: limParas, verticalAlign: DocxVerticalAlign.TOP, width: { size: widths.limitation, type: DocxWidthType.PERCENTAGE } }));
        cells.push(new DocxTableCell({ children: analysisParas, verticalAlign: DocxVerticalAlign.TOP, width: { size: widths.argument, type: DocxWidthType.PERCENTAGE } }));
        if (columns.rebuttal) {
          const rebuttalParas = lim.rebuttal
            ? buildBodyParagraphs(lim.rebuttal, lim, false)
            : [seeParagraph(rebuttalPlaceholder(lim))];
          if (rebuttalParas.length === 0) rebuttalParas.push(new DocxParagraph({ children: [] }));
          cells.push(new DocxTableCell({
            children: rebuttalParas, verticalAlign: DocxVerticalAlign.TOP,
            shading: { fill: 'FBFBFD' }, width: { size: widths.rebuttal, type: DocxWidthType.PERCENTAGE },
          }));
        }
        claimRows.push(new DocxTableRow({ children: cells }));
      });
    });

    const headerCell = (text, size) => new DocxTableCell({
      children: [new DocxParagraph({ children: [new DocxTextRun({ text, bold: true })] })],
      shading: { fill: 'EEF2F7' }, width: { size, type: DocxWidthType.PERCENTAGE },
    });
    const table = new DocxTable({
      width: { size: 100, type: DocxWidthType.PERCENTAGE },
      // Table-wide default cell padding -- applies to every cell unless a cell
      // overrides it itself (none here do). Word's own built-in default is far
      // tighter (0 top/bottom, ~108 dxa/~0.075in left/right), which read as
      // cramped once real paragraph text/citations filled the cells. Values are
      // dxa (twentieths of a point): 120 ≈ 6pt vertical, 180 ≈ 9pt horizontal.
      margins: { top: 120, bottom: 120, left: 180, right: 180 },
      rows: [
        new DocxTableRow({
          tableHeader: true,
          children: [
            headerCell('Claim', widths.claim),
            headerCell('Limitation', widths.limitation),
            headerCell(columns.argument, widths.argument),
            ...(columns.rebuttal ? [headerCell(columns.rebuttal, widths.rebuttal)] : []),
          ],
        }),
        ...claimRows,
      ],
    });

    const docChildren = [
      new DocxParagraph({ text: `${patentName} — ${useCaseLabel} Claim Chart`, heading: DocxHeadingLevel.HEADING_1 }),
      new DocxParagraph({
        children: [new DocxTextRun({
          text: `Generated ${now} · Perspective: ${useCaseLabel}`,
          color: '666666', size: 18,
        })],
        spacing: { after: 200 },
      }),
    ];
    // The notice as a one-cell shaded table, so it reads as a boxed callout in
    // Word the same way it does in the PDF and the live table.
    const disclaimerParas = [
      new DocxParagraph({ children: [new DocxTextRun({ text: CLAIM_CHART_DISCLAIMER.title.toUpperCase(), bold: true })], spacing: { after: 80 } }),
    ];
    CLAIM_CHART_DISCLAIMER.sections.forEach((sec) => {
      disclaimerParas.push(new DocxParagraph({ children: [new DocxTextRun({ text: sec.heading, bold: true, size: 18 })], spacing: { before: 100, after: 40 } }));
      sec.paragraphs.forEach((para) => disclaimerParas.push(new DocxParagraph({ children: [new DocxTextRun({ text: para, size: 18 })], spacing: { after: 40 } })));
    });
    disclaimerParas.push(new DocxParagraph({ children: [new DocxTextRun({ text: CLAIM_CHART_DISCLAIMER.footer, italics: true, color: '555555', size: 18 })], spacing: { before: 100 } }));
    docChildren.push(new DocxTable({
      width: { size: 100, type: DocxWidthType.PERCENTAGE },
      margins: { top: 120, bottom: 120, left: 180, right: 180 },
      rows: [new DocxTableRow({ children: [new DocxTableCell({ children: disclaimerParas, shading: { fill: 'FFFBEA' } })] })],
    }));
    docChildren.push(new DocxParagraph({ text: '', spacing: { after: 200 } }));
    if (refNames.length > 0) {
      docChildren.push(new DocxParagraph({ children: [new DocxTextRun({ text: 'Prior Art References', bold: true })], spacing: { after: 40 } }));
      refNames.forEach((n) => docChildren.push(new DocxParagraph({ children: [new DocxTextRun({ text: n })] })));
      docChildren.push(new DocxParagraph({ text: '', spacing: { after: 200 } }));
    }
    if (columns.leadIn) {
      docChildren.push(new DocxParagraph({ children: [new DocxTextRun({ text: columns.leadIn })], spacing: { after: 120 } }));
    }
    docChildren.push(table);

    const doc = new DocxDocument({
      sections: [{
        properties: { page: { size: { orientation: 'landscape' } }, margin: { top: 720, bottom: 720, left: 720, right: 720 } },
        children: docChildren,
      }],
    });

    const blob = await DocxPacker.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${patentName} - ${useCaseLabel} Claim Chart.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // --- Sufficiency Check handler ---
  const handleSufficiencyCheck = async () => {
    if (!suffIndex) return;
    setSuffLoading(true);
    setSuffError(null);
    setSuffLimitations([]);
    setSuffClaimLevel({});
    setSuffTotal(0);
    try {
      const res = await authFetch('/sufficiency', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patentBody(suffIndex, { phosita: suffPhosita, max_claim: suffMaxClaim || null })),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line) continue;
          let msg;
          try { msg = JSON.parse(line); } catch (e) { console.error('Sufficiency stream parse error:', e, line.slice(0, 200)); continue; }
          if (msg.type === 'total') {
              setSuffTotal(msg.count);
            } else if (msg.type === 'limitation') {
            setSuffLimitations((prev) => {
              const next = [...prev, msg];
              next.sort((a, b) =>
                a.claim_number !== b.claim_number
                  ? a.claim_number - b.claim_number
                  : a.limitation_index - b.limitation_index
              );
              return next;
            });
          } else if (msg.type === 'claim_level') {
            setSuffClaimLevel((prev) => ({ ...prev, [msg.claim_number]: msg }));
          } else if (msg.type === 'done') {
            setSuffLoading(false);
          } else if (msg.type === 'error') {
            setSuffError(msg.message);
            setSuffLoading(false);
          }
        }
      }
    } catch (err) {
      setSuffError(err.message);
    } finally {
      setSuffLoading(false);
    }
  };

  // Once the uploaded/matched "Patent" (later) patent finishes ingesting, activate it for
  // the claim chart. Each "Prior Art" slot activates itself via PriorArtSlot's own effect,
  // reporting into priorArtSlotDocIds instead of a single synced index.
  useEffect(() => {
    if (priorArtUpload2.doc && priorArtUpload2.doc.status === 'ready' && priorArtIndex2 !== priorArtUpload2.doc.id) {
      setPriorArtIndex2(priorArtUpload2.doc.id);
    }
  }, [priorArtUpload2.doc, priorArtIndex2]);

  // Clear a previously-generated chart as soon as either the "Patent" (later)
  // or any "Prior Art" (earlier) document selection changes -- otherwise the
  // old results stay on screen looking like they cover the newly-picked
  // document(s) until the user notices and clicks Generate again. Mirrors the
  // existing clear-on-change for the Perspective dropdown (see its onChange
  // below), just triggered by document identity instead of a dropdown value.
  useEffect(() => {
    setPriorArtResults([]);
    setPriorArtError(null);
    setPriorArtTotal(0);
  }, [priorArtIndex2, priorArtIndexList]);

  // --- Prior Art (claim chart) handlers ---
  const handlePriorArtCheck = async () => {
    if (priorArtIndexList.length === 0 || !priorArtIndex2 || priorArtSelectedClaims.size === 0) return;

    setPriorArtLoading(true);
    setPriorArtError(null);
    setPriorArtResults([]);
    setPriorArtTotal(0);

    // Only send a filter when it actually narrows the set -- every claim
    // toggled on (the default) omits the field entirely so the server's own
    // "no filter" semantics apply, rather than relying on this array exactly
    // mirroring priorArtClaimNumbers (e.g. if it hasn't loaded yet).
    const claimNumbersParam = (priorArtClaimNumbers.length > 0 && priorArtSelectedClaims.size < priorArtClaimNumbers.length)
      ? Array.from(priorArtSelectedClaims).sort((a, b) => a - b)
      : undefined;

    try {
      const res = await authFetch('/claimchart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          earlier: priorArtIndexList,
          later: priorArtIndex2,
          model: priorArtModel,
          use_case: priorArtUseCase,
          ...(claimNumbersParam ? { claim_numbers: claimNumbersParam } : {}),
        }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line) continue;
          const msg = JSON.parse(line);
          if (msg.type === 'total') {
            setPriorArtTotal(msg.count);
          } else if (msg.type === 'claim') {
            setPriorArtResults((prev) => [...prev, {
              claim_id: msg.claim_id,
              claim_number: msg.claim_number,
              claim_text: msg.claim_text,
              claim_source: msg.claim_source || {},
              limitations: msg.limitations || [],
              verdict: msg.verdict || null,
            }].sort((a, b) => a.claim_number - b.claim_number));
          } else if (msg.type === 'error') {
            // A per-claim failure (has claim_number) doesn't stop the rest of the
            // stream server-side (see the /claimchart generator), so don't kill the
            // loading/progress state here either -- just surface the message and let
            // remaining claims keep streaming in.
            setPriorArtError(msg.message);
          }
        }
      }
    } catch (err) {
      setPriorArtError(err.message);
    } finally {
      setPriorArtLoading(false);
    }
  };

  // --- PDF modal state ---
  const [pdfModal, setPdfModal] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [renderedPages, setRenderedPages] = useState(new Set());
  const [showSummary, setShowSummary] = useState(false);
  const [pdfSearchTerm, setPdfSearchTerm] = useState('');
  const [pdfSearchResults, setPdfSearchResults] = useState([]); // [{pageNum, text}]
  const [pdfSearchIdx, setPdfSearchIdx] = useState(0);
  const scrollRef = useRef(null);

  // Refined per-limitation highlights: server bboxes for middle lines,
  // PDF.js used only to trim word boundaries on the first and last line.
  const [preciseSuffHighlights, setPreciseSuffHighlights] = useState({});
  const preciseProcessedRef = useRef(null);

  useEffect(() => {
    if (!pdfDoc || suffLoading || !suffLimitations.length || !pdfModal?.filename) return;
    const filename = pdfModal.filename;
    const lims = suffLimitations.filter(l =>
      l.claim_source?.filename === filename && l.limitation_bboxes?.length > 0
    );
    if (!lims.length) return;
    const cacheKey = `${filename}:${lims.length}`;
    if (preciseProcessedRef.current === cacheKey) return;
    preciseProcessedRef.current = cacheKey;

    // Start from the server line bboxes and refine first/last line edges using PDF.js.
    // result: pageNum → [{bbox, color}]
    const result = {};

    // Helper: add a colored bbox to result
    const addHit = (pageNum, bbox, color) => {
      if (!result[pageNum]) result[pageNum] = [];
      result[pageNum].push({ bbox, color });
    };

    // Build initial highlights from server bboxes (line-level, all lines correct).
    // Adequate limitations aren't highlighted at all — only concern/deficient are.
    for (const lim of lims) {
      const statusKey = (lim.status || 'concern').toLowerCase();
      if (statusKey === 'adequate') continue;
      const color = _SUFF_STATUS_COLOR[statusKey] || _SUFF_STATUS_COLOR.concern;
      for (const [pageNum, bbox] of lim.limitation_bboxes) {
        addHit(pageNum, bbox, color);
      }
    }

    // Now refine first/last line edges with PDF.js in the background.
    let cancelled = false;
    (async () => {
      // Collect the pages we need to process (pages that have first or last line bboxes).
      const pagesToProcess = new Set();
      for (const lim of lims) {
        const bb = lim.limitation_bboxes;
        if (bb.length >= 1) pagesToProcess.add(bb[0][0]);           // first line page
        if (bb.length >= 2) pagesToProcess.add(bb[bb.length - 1][0]); // last line page
      }

      // Fetch text content for only the relevant pages.
      const pageTextItems = {};
      for (const pageNum of pagesToProcess) {
        if (cancelled) return;
        try {
          const page = await pdfDoc.getPage(pageNum);
          const vp = page.getViewport({ scale: 1 });
          const tc = await page.getTextContent();
          pageTextItems[pageNum] = { items: tc.items, vp };
        } catch (_) { /* skip */ }
        await new Promise(r => setTimeout(r, 0));
      }

      if (cancelled) return;

      // For each limitation, try to refine its first and last line.
      for (const lim of lims) {
        const bb = lim.limitation_bboxes;
        if (!bb.length) continue;
        const statusKey = (lim.status || 'concern').toLowerCase();
        if (statusKey === 'adequate') continue;
        const color = _SUFF_STATUS_COLOR[statusKey] || _SUFF_STATUS_COLOR.concern;
        const limNorm = _normText(lim.limitation);

        // Helper: get all text items near a line bbox's top coordinate.
        const itemsNearLine = (pageNum, lineBbox) => {
          const pd = pageTextItems[pageNum];
          if (!pd) return null;
          const { items, vp } = pd;
          const lineTopNorm = (vp.height - lineBbox.top * vp.height - lineBbox.height * vp.height) / vp.height;
          // Actually convert back: lineBbox.top is already 0-1 top-down.
          // PDF.js y is bottom-up, so item y = vp.height - (bbox.top + bbox.height) * vp.height.
          // Tolerance must stay well under one line height — adjacent lines are typically
          // spaced ~1x line height apart, so anything looser pulls neighboring lines' text
          // into this line's reconstructed string and corrupts the char-offset trim below.
          const tolerance = lineBbox.height * 0.5;
          // In a two-column layout, a left-column line and a right-column line can sit at the
          // exact same y — a y-only filter pulls the wrong column's text into the
          // reconstructed line string. Restrict to items on the same side of the column gutter
          // as the target line, but only when the line itself is narrow enough to indicate a
          // column layout in the first place — a full-width single-column line legitimately
          // has words on both sides of the page midpoint.
          const COLUMN_SPLIT = 0.5;
          const isColumnLayout = lineBbox.width < 0.45;
          const sameSide = (left) => !isColumnLayout || (left < COLUMN_SPLIT) === (lineBbox.left < COLUMN_SPLIT);
          return items
            .filter(it => it.str && it.str.trim())
            .map(it => {
              const [, , , , x, y] = it.transform;
              const h = it.height || Math.abs(it.transform[3]) || 10;
              const topNorm = (vp.height - y - h) / vp.height;
              return { str: it.str, normStr: _normText(it.str), left: x / vp.width, top: topNorm, width: (it.width || 0) / vp.width, height: h / vp.height };
            })
            .filter(it => Math.abs(it.top - lineBbox.top) < tolerance && sameSide(it.left))
            .sort((a, b) => a.left - b.left);
        };

        // PDF.js doesn't always tokenize per word — sometimes a whole line comes back as one
        // item. Jumping to item boundaries can't sub-divide that, so interpolate the x position
        // of a character offset proportionally across whichever item contains it (same
        // approximation the backend already uses for word-bbox highlighting). This assumes
        // uniform character width, which real proportional fonts don't have, so the estimate
        // can land a hair inside the boundary glyph (e.g. clipping the "w" of "wherein").
        // `direction` backs the edge off outward by a fraction of a character to avoid that.
        const charIndexToX = (items, charIdx, direction) => {
          let charCount = 0;
          for (const it of items) {
            const start = charCount;
            const end = charCount + it.normStr.length;
            if (charIdx <= end) {
              const within = Math.max(0, Math.min(it.normStr.length, charIdx - start));
              const frac = it.normStr.length > 0 ? within / it.normStr.length : 0;
              const avgCharWidth = it.normStr.length > 0 ? it.width / it.normStr.length : 0;
              const backoff = avgCharWidth * 0.4;
              const x = it.left + frac * it.width;
              return direction === 'start' ? x - backoff : x + backoff;
            }
            charCount = end + 1; // +1 for the space joiner between items
          }
          const last = items[items.length - 1];
          return last.left + last.width;
        };

        // Refine the FIRST line: trim the left edge to where the limitation text starts.
        const [firstPage, firstBbox] = bb[0];
        const firstItems = itemsNearLine(firstPage, firstBbox);
        if (firstItems && firstItems.length) {
          const lineText = firstItems.map(i => i.normStr).join(' ');
          // A limitation boundary frequently falls mid-line (e.g. "...is provided, wherein
          // the..." — one limitation ends and the next begins on the same physical PDF line).
          // A long anchor overflows past this line's own text into the next line and never
          // matches, so try progressively shorter anchors until one fits within this line.
          let idx = -1;
          for (const len of [40, 28, 20, 14, 10, 7]) {
            const found = lineText.indexOf(limNorm.slice(0, len));
            if (found !== -1) { idx = found; break; }
          }
          if (idx !== -1) {
            const startLeft = charIndexToX(firstItems, idx, 'start');
            const refinedFirst = { ...firstBbox, left: startLeft, width: (firstBbox.left + firstBbox.width) - startLeft };
            // Replace the first-line entry in result.
            if (result[firstPage]) {
              const ri = result[firstPage].findIndex(h => h.bbox === firstBbox);
              if (ri !== -1) result[firstPage][ri] = { bbox: refinedFirst, color };
            }
          } else if (bb.length > 1 && result[firstPage]) {
            // No anchor of any length matched this line at all — none of this limitation's
            // own text is on it (it's a boundary line that's essentially all the *previous*
            // limitation's tail, e.g. "that the active application..." where only "that" is
            // a dropped connective word and everything else belongs to the next limitation).
            // Falling back to the untrimmed full-width line would double-draw it on top of
            // whichever neighbor actually matched here, so drop it instead — but only when
            // this limitation has other lines of its own; for a single-line limitation this
            // is the only evidence we have, so keep the untrimmed fallback.
            const ri = result[firstPage].findIndex(h => h.bbox === firstBbox);
            if (ri !== -1) result[firstPage].splice(ri, 1);
          }
        }

        // Refine the LAST line: trim the right edge to where the limitation text ends.
        if (bb.length > 1) {
          const [lastPage, lastBbox] = bb[bb.length - 1];
          const lastItems = itemsNearLine(lastPage, lastBbox);
          if (lastItems && lastItems.length) {
            const lineText = lastItems.map(i => i.normStr).join(' ');
            // Same reasoning as the first-line anchor above, mirrored from the end.
            let endOfLim = '', idx = -1;
            for (const len of [40, 28, 20, 14, 10, 7]) {
              const candidate = limNorm.slice(-len);
              const found = lineText.lastIndexOf(candidate);
              if (found !== -1) { endOfLim = candidate; idx = found; break; }
            }
            if (idx !== -1) {
              const endRight = charIndexToX(lastItems, idx + endOfLim.length, 'end');
              const refinedLast = { ...lastBbox, width: endRight - lastBbox.left };
              if (result[lastPage]) {
                const ri = result[lastPage].findIndex(h => h.bbox === lastBbox);
                if (ri !== -1) result[lastPage][ri] = { bbox: refinedLast, color };
              }
            } else if (result[lastPage]) {
              // No anchor matched — none of this limitation's own text is on this line (it's
              // effectively all the *next* limitation's opening text). Drop it rather than
              // fall back to the untrimmed full-width line, which would double-draw on top of
              // whatever the next limitation's first-line trim correctly claims here.
              const ri = result[lastPage].findIndex(h => h.bbox === lastBbox);
              if (ri !== -1) result[lastPage].splice(ri, 1);
            }
          }
        }
      }

      if (!cancelled) setPreciseSuffHighlights(prev => ({ ...prev, [filename]: result }));
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, suffLimitations, suffLoading, pdfModal?.filename]);

  const handlePageRendered = useCallback((pageNum) => {
    setRenderedPages((prev) => {
      const next = new Set(prev);
      next.add(pageNum);
      return next;
    });
  }, []);

  // --- Q&A handlers ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setAnswer(null);

    try {
      const params = new URLSearchParams({ query: query.trim(), stream: '1', model: selectedModel });
      if (selectedPortfolioId) params.set('portfolio_id', selectedPortfolioId);
      else if (selectedIndex) params.set('index_name', selectedIndex);
      const res = await authFetch(`/answer?${params}`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line) continue;
          const msg = JSON.parse(line);
          if (msg.type === 'sources') {
            setAnswer({ query: msg.query, answer: '', sources: msg.sources });
          } else if (msg.type === 'error') {
            setError(msg.message);
          } else if (msg.type === 'answer_chunk') {
            setAnswer((prev) => ({ ...prev, answer: prev.answer + msg.text }));
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Chat handlers ---
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMsg = chatInput.trim();
    setChatInput('');
    const newUserMessage = { role: 'user', content: userMsg };
    setChatMessages((prev) => [...prev, newUserMessage]);
    setChatLoading(true);

    try {
      // Build full conversation history (user + assistant only) plus new message
      const allMessages = [...chatMessages, newUserMessage].filter(
        (m) => m.role === 'user' || m.role === 'assistant'
      );

      const res = await authFetch('/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: allMessages,
          ...(selectedPortfolioId ? { portfolio_id: selectedPortfolioId } : { index_name: selectedIndex }),
          stream: 1,
          reasoning: reasoning || null,
        }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      // Add an empty assistant message that we'll stream into
      setChatMessages((prev) => [...prev, { role: 'assistant', content: '', sources: [] }]);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let citationHold = ''; // hold back trailing incomplete citation like "[5"

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Flush any held-back text
          if (citationHold) {
            const held = citationHold;
            setChatMessages((prev) => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              updated[updated.length - 1] = { ...last, content: last.content + held };
              return updated;
            });
          }
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line) continue;
          const msg = JSON.parse(line);
          if (msg.type === 'sources') {
            setChatMessages((prev) => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              updated[updated.length - 1] = { ...last, sources: [...(last.sources || []), ...msg.sources] };
              return updated;
            });
          } else if (msg.type === 'error') {
            setChatMessages((prev) => [...prev, { role: 'error', content: msg.message }]);
          } else if (msg.type === 'answer_chunk') {
            let text = citationHold + msg.text;
            citationHold = '';
            // If text ends with an incomplete citation, hold it back
            const trailingBracket = text.lastIndexOf('[');
            if (trailingBracket !== -1 && !text.slice(trailingBracket).includes(']')) {
              citationHold = text.slice(trailingBracket);
              text = text.slice(0, trailingBracket);
            }
            if (text) {
              setChatMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                updated[updated.length - 1] = { ...last, content: last.content + text };
                return updated;
              });
            }
          }
        }
      }
    } catch (err) {
      setChatMessages((prev) => [...prev, { role: 'error', content: err.message }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Auto-scroll chat to bottom on new messages.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // --- Discrep handlers ---
  const handleDiscrepSubmit = async (e) => {
    e.preventDefault();
    if (!discrepInput.trim()) return;

    setDiscrepLoading(true);
    setDiscrepError(null);
    setDiscrepItems([]);
    setDiscrepSources([]);
    setDiscrepSubmitted(true);

    try {
      const res = await authFetch('/discrepancy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: discrepInput.trim(),
          title: discrepTitle.trim(),
          ...(selectedPortfolioId ? { portfolio_id: selectedPortfolioId } : { index_name: selectedIndex }),
          model: discrepModel,
          stream: 1,
        }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line) continue;
          const msg = JSON.parse(line);
          if (msg.type === 'sources') {
            setDiscrepSources(msg.sources);
          } else if (msg.type === 'discrepancies') {
            setDiscrepItems(msg.discrepancies);
          } else if (msg.type === 'error') {
            setDiscrepError(msg.message);
          }
        }
      }
    } catch (err) {
      setDiscrepError(err.message);
    } finally {
      setDiscrepLoading(false);
    }
  };

  // --- PDF modal handlers ---
  const handleSourceClick = useCallback((source, { showSummary: initSummary = false, emphasisBboxes = null, highlightColor = null, suppressHighlight = false } = {}) => {
    if (!source.filename || !source.bboxes || source.bboxes.length === 0) return;
    setPdfModal({
      filename: source.filename, bboxes: source.bboxes, title: source.title,
      number: source.number, summary: source.summary,
      emphasisBboxes: emphasisBboxes,
      highlightColor: highlightColor,
      // Used only to scroll to the right page — suppressHighlight skips drawing this as
      // a rectangle. Sufficiency claim clicks set this since coloredHighlights (the
      // persistent per-limitation fills) already highlight the claim more precisely.
      suppressHighlight: suppressHighlight,
    });
    setShowSummary(initSummary);
  }, []);

  const sourcesByNumber = useMemo(() => {
    if (!answer?.sources) return {};
    const map = {};
    for (const s of answer.sources) map[s.number] = s;
    return map;
  }, [answer?.sources]);

  const discrepSourcesByNumber = useMemo(() => {
    const map = {};
    for (const s of discrepSources) map[s.number] = s;
    return map;
  }, [discrepSources]);

  const sortedDiscrepItems = useMemo(() =>
    [...discrepItems].sort((a, b) => (b.severity ?? 0) - (a.severity ?? 0)),
    [discrepItems]
  );

  const closeModal = useCallback(() => {
    setPdfModal(null);
    setPdfDoc(null);
    setShowSummary(false);
    setRenderedPages(new Set());
    setPdfSearchTerm('');
    setPdfSearchResults([]);
    setPdfSearchIdx(0);
  }, []);

  const bboxesByPage = useMemo(() => {
    if (!pdfModal || pdfModal.suppressHighlight) return {};
    const map = {};
    for (const [pageNum, bbox] of pdfModal.bboxes) {
      if (!map[pageNum]) map[pageNum] = [];
      map[pageNum].push(bbox);
    }
    return map;
  }, [pdfModal]);

  const emphasisBboxesByPage = useMemo(() => {
    if (!pdfModal || !pdfModal.emphasisBboxes) return {};
    const map = {};
    for (const [pageNum, bbox] of pdfModal.emphasisBboxes) {
      if (!map[pageNum]) map[pageNum] = [];
      map[pageNum].push(bbox);
    }
    return map;
  }, [pdfModal]);

  // Per-limitation colored highlights: filename → pageNum → [{bbox, color}]
  // Uses per-limitation bboxes (from paragraph matching) when available,
  // falling back to the whole claim chunk bboxes.
  const suffColoredHighlightsByPage = useMemo(() => {
    if (!suffLimitations.length) return {};
    // Use worst-color per unique bbox to avoid overlapping highlights.
    // Key: filename → pageNum → bboxKey → {bbox, color, sev}
    const staging = {};
    for (const lim of suffLimitations) {
      const filename = lim.claim_source?.filename;
      if (!filename) continue;
      const statusKey = (lim.status || 'concern').toLowerCase();
      if (statusKey === 'adequate') continue;
      const bboxes = lim.limitation_bboxes?.length
        ? lim.limitation_bboxes
        : (lim.claim_source?.bboxes || []);
      if (!bboxes.length) continue;
      const sev   = _SEVERITY[statusKey] ?? 1;
      const color = _SUFF_STATUS_COLOR[statusKey] || _SUFF_STATUS_COLOR.concern;
      if (!staging[filename]) staging[filename] = {};
      for (const [pageNum, bbox] of bboxes) {
        if (!staging[filename][pageNum]) staging[filename][pageNum] = {};
        const key = `${bbox.left?.toFixed(4)},${bbox.top?.toFixed(4)}`;
        const ex = staging[filename][pageNum][key];
        if (!ex || sev > ex.sev) staging[filename][pageNum][key] = { bbox, color, sev };
      }
    }
    // Flatten to the [{bbox, color}] arrays PdfPage expects.
    const result = {};
    for (const [fn, pages] of Object.entries(staging)) {
      result[fn] = {};
      for (const [pn, bboxMap] of Object.entries(pages)) {
        result[fn][pn] = Object.values(bboxMap).map(({ bbox, color }) => ({ bbox, color }));
      }
    }
    return result;
  }, [suffLimitations]);

  const searchHighlightsByPage = useMemo(() => {
    const map = {};
    for (const { pageNum, bboxes } of pdfSearchResults) {
      map[pageNum] = bboxes;
    }
    return map;
  }, [pdfSearchResults]);

  const firstHighlightPage = useMemo(() => {
    if (!pdfModal) return 1;
    // Prefer scrolling to the emphasis (evidence paragraph) page if available
    if (pdfModal.emphasisBboxes && pdfModal.emphasisBboxes.length > 0) return pdfModal.emphasisBboxes[0][0];
    if (pdfModal.bboxes.length === 0) return 1;
    return pdfModal.bboxes[0][0];
  }, [pdfModal]);

  useEffect(() => {
    if (!pdfModal) return;
    let cancelled = false;
    authFetch(`/documents/${encodeURIComponent(pdfModal.filename)}`)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load PDF: ${res.status}`);
        return res.arrayBuffer();
      })
      .then(buf => {
        if (!cancelled) return pdfjsLib.getDocument({ data: buf }).promise;
      })
      .then(pdf => {
        if (!cancelled && pdf) setPdfDoc(pdf);
      })
      .catch(err => {
        if (!cancelled) console.error('PDF load error:', err);
      });
    return () => { cancelled = true; };
  }, [pdfModal]);

  useEffect(() => {
    if (!pdfDoc || !scrollRef.current) return;
    const timer = setTimeout(() => {
      const el = scrollRef.current?.querySelector(
        `[data-page="${firstHighlightPage}"]`,
      );
      if (el) el.scrollIntoView({ block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [pdfDoc, firstHighlightPage]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const wrappers = container.querySelectorAll('.pdf-page-wrapper');
    const containerTop = container.scrollTop;
    let best = 1;
    let bestDist = Infinity;
    for (const el of wrappers) {
      const dist = Math.abs(el.offsetTop - containerTop);
      if (dist < bestDist) {
        bestDist = dist;
        best = parseInt(el.dataset.page, 10);
      }
    }
    setCurrentPage(best);
  }, []);

  const pageNumbers = pdfDoc
    ? Array.from({ length: pdfDoc.numPages }, (_, i) => i + 1)
    : [];

  const handlePdfSearch = useCallback(async (term) => {
    setPdfSearchTerm(term);
    if (!term || !pdfModal?.filename) { setPdfSearchResults([]); setPdfSearchIdx(0); return; }
    try {
      const resp = await authFetch(`/search_doc?filename=${encodeURIComponent(pdfModal.filename)}&query=${encodeURIComponent(term)}`);
      const data = await resp.json();
      const results = data.results.map(r => ({ pageNum: r.page_num, bboxes: r.bboxes }));
      setPdfSearchResults(results);
      setPdfSearchIdx(0);
      if (results.length > 0) {
        const el = scrollRef.current?.querySelector(`[data-page="${results[0].pageNum}"]`);
        if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    } catch (e) {
      console.error('search_doc error:', e);
    }
  }, [pdfModal?.filename]);

  const navigatePdfSearch = useCallback((dir) => {
    if (pdfSearchResults.length === 0) return;
    const next = (pdfSearchIdx + dir + pdfSearchResults.length) % pdfSearchResults.length;
    setPdfSearchIdx(next);
    const el = scrollRef.current?.querySelector(`[data-page="${pdfSearchResults[next].pageNum}"]`);
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [pdfSearchResults, pdfSearchIdx]);

  useEffect(() => {
    if (location.pathname === '/chat') setTab('chat');
    else if (location.pathname === '/sufficiency') setTab('sufficiency');
    else if (location.pathname === '/admin' && isAdmin) {
      setActivityLoading(true);
      setActivityError(null);
      authFetch('/admin/activity')
        .then((r) => r.json())
        .then((data) => { setActivityLog(data); setActivityLoading(false); })
        .catch((e) => { setActivityError(e.message); setActivityLoading(false); });
    } else if (location.pathname === '/admin/users' && isAdmin) {
      setAdminUsersLoading(true);
      setAdminUsersError(null);
      authFetch('/admin/users')
        .then((r) => r.json())
        .then((data) => { setAdminUsers(data); setAdminUsersLoading(false); })
        .catch((e) => { setAdminUsersError(e.message); setAdminUsersLoading(false); });
    }
  }, [location.pathname, isAdmin, authFetch]);

  // --- Portfolio management ---
  const fetchMgmtDocuments = useCallback((portfolioId) => {
    setMgmtLoading(true);
    setMgmtError(null);
    authFetch(`/portfolios/${portfolioId}/documents`)
      .then((r) => r.json())
      .then((data) => setMgmtDocuments(data.documents || []))
      .catch((err) => setMgmtError(err.message))
      .finally(() => setMgmtLoading(false));
  }, [authFetch]);

  useEffect(() => {
    if (!mgmtPortfolioId) { setMgmtDocuments([]); return; }
    fetchMgmtDocuments(mgmtPortfolioId);
  }, [mgmtPortfolioId, fetchMgmtDocuments]);

  // Poll while any document is pending/running.
  useEffect(() => {
    if (!mgmtPortfolioId) return;
    const hasPending = mgmtDocuments.some((d) => d.status === 'pending' || d.status === 'running');
    if (!hasPending) return;
    const timer = setInterval(() => {
      authFetch(`/portfolios/${mgmtPortfolioId}/documents`)
        .then((r) => r.json())
        .then((data) => {
          const docs = data.documents || [];
          setMgmtDocuments(docs);
          // Refresh patent picker options when a doc becomes ready.
          if (docs.some((d) => d.status === 'ready')) fetchPortfolios();
        })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [mgmtPortfolioId, mgmtDocuments, authFetch, fetchPortfolios]);

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    if (!newPortfolioName.trim()) return;
    try {
      const res = await authFetch('/portfolios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newPortfolioName.trim() }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setPortfolios((prev) => [...prev, { ...data, is_owner: true }]);
      setMgmtPortfolioId(data.id);
      setNewPortfolioName('');
      setCreatingPortfolio(false);
    } catch (err) {
      alert(`Failed to create portfolio: ${err.message}`);
    }
  };

  const handleTogglePublic = async (id, currentValue) => {
    try {
      const res = await authFetch(`/portfolios/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_public: !currentValue }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setPortfolios((prev) => prev.map((p) => p.id === id ? { ...p, is_public: !currentValue } : p));
    } catch (err) {
      alert(`Failed to update portfolio: ${err.message}`);
    }
  };

  const handleDeletePortfolio = async (id) => {
    if (!window.confirm('Delete this portfolio and all its documents?')) return;
    try {
      const res = await authFetch(`/portfolios/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setPortfolios((prev) => prev.filter((p) => p.id !== id));
      if (mgmtPortfolioId === id) { setMgmtPortfolioId(null); setMgmtDocuments([]); }
    } catch (err) {
      alert(`Failed to delete portfolio: ${err.message}`);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !mgmtPortfolioId) return;
    setUploadLoading(true);
    setUploadError(null);
    const fd = new FormData();
    fd.append('file', uploadFile);
    if (uploadName.trim()) fd.append('name', uploadName.trim());
    try {
      const res = await authFetch(`/portfolios/${mgmtPortfolioId}/documents`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error: ${res.status}`);
      }
      const doc = await res.json();
      setMgmtDocuments((prev) => [...prev, doc]);
      setUploadFile(null);
      setUploadName('');
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const res = await authFetch(`/portfolios/${mgmtPortfolioId}/documents/${docId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setMgmtDocuments((prev) => prev.filter((d) => d.id !== docId));
      fetchPortfolios();
    } catch (err) {
      alert(`Failed to delete document: ${err.message}`);
    }
  };

  const handleRetryIngest = async (doc) => {
    if (doc.status === 'ready' && !window.confirm('Re-process this document? This will delete its existing data and re-run ingestion from scratch.')) return;
    try {
      const res = await authFetch(`/portfolios/${mgmtPortfolioId}/documents/${doc.id}/retry`, { method: 'POST' });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error: ${res.status}`);
      }
      setMgmtDocuments((prev) => prev.map((d) => d.id === doc.id ? { ...d, status: 'pending', log: null } : d));
    } catch (err) {
      alert(`Failed to re-process document: ${err.message}`);
    }
  };

  if (isLoading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 18, color: '#888' }}>Loading…</div>;
  }

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f5' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '48px 56px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', textAlign: 'center', minWidth: 320 }}>
          <img src={gavilLogo} alt="Gavil" style={{ height: 48, width: 'auto', marginBottom: 16 }} />
          <div style={{ color: '#666', marginBottom: authError ? 16 : 32 }}>Sign in to continue</div>
          {authError && (
            <div style={{ color: '#c0392b', background: '#fdf0ef', border: '1px solid #f5c6c2', borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: 13, textAlign: 'left' }}>
              {authError.message}
            </div>
          )}
          <button
            onClick={() => {
              const params = { authorizationParams: { prompt: authError ? 'login' : undefined } };
              console.log('[Auth0] loginWithRedirect params:', params);
              loginWithRedirect(params);
            }}
            style={{ background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontSize: 15, cursor: 'pointer', width: '100%' }}
          >
            {authError ? 'Try a different account' : 'Sign in'}
          </button>
        </div>
      </div>
    );
  }

  const userInitials = (user?.name || user?.email || '?')
    .split(/[\s@]+/).filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');

  const userMenu = (
    <div ref={userMenuRef} style={{ marginLeft: 'auto', position: 'relative' }}>
      <div
        onClick={() => setUserMenuOpen(o => !o)}
        style={{
          width: 34, height: 34, borderRadius: '50%', background: '#1e2d5a',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 600, cursor: 'pointer', userSelect: 'none',
        }}
      >
        {userInitials}
      </div>
      {userMenuOpen && (
        <div
          style={{
            position: 'absolute', right: 0, top: 42, background: '#fff',
            border: '1px solid #e5e5e5', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            minWidth: 200, zIndex: 1000, overflow: 'hidden',
          }}
          onMouseLeave={() => setUserMenuOpen(false)}
        >
          <div style={{ padding: '10px 16px', fontSize: 12, color: '#888', borderBottom: '1px solid #f0f0f0' }}>
            {user?.email}
          </div>
          <div
            onClick={() => { alert('To add more credits, please contact us at admin@gavil.ai.'); setUserMenuOpen(false); }}
            style={{ padding: '10px 16px', fontSize: 14, cursor: 'pointer', color: '#333' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.background = ''}
          >
            Add more credits
          </div>
          <div
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            style={{ padding: '10px 16px', fontSize: 14, cursor: 'pointer', color: '#333', borderTop: '1px solid #f0f0f0' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.background = ''}
          >
            Sign out
          </div>
        </div>
      )}
    </div>
  );

  const landingPage = (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <header className="app-header">
        <img src={gavilLogo} alt="Gavil" className="app-logo" />
        {userMenu}
      </header>
      {userCredits !== null && (
        <div style={{ position: 'fixed', bottom: 24, right: 28, fontSize: 13, color: '#888' }}>
          {userCredits.toLocaleString()} credits remaining
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 32, paddingTop: 120 }}>
        {[
          { label: 'Sufficiency Checking', path: '/sufficiency', desc: '§ 112 written description analysis' },
          { label: 'Claim Chart', path: '/demo', desc: 'Compare claims across patents', tabKey: 'priorart' },
          { label: 'Demo Features', path: '/demo', desc: 'Full patent analysis suite', tabKey: 'qa' },
        ].map(({ label, path, desc, tabKey }) => (
          <div
            key={label}
            onClick={() => { navigate(path); if (tabKey) setTab(tabKey); }}
            style={{
              width: 200, height: 160, background: '#fff', borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 12, transition: 'box-shadow 0.15s',
              padding: 24, textAlign: 'center',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'}
          >
            <div style={{ fontSize: 20, fontWeight: 700 }}>{label}</div>
            <div style={{ fontSize: 13, color: '#888', lineHeight: 1.4 }}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const sidebarNavItems = [
    { key: 'sufficiency',  label: 'Sufficiency',      path: '/sufficiency' },
    { key: 'priorart',     label: 'Claim Chart',      path: '/demo', tabKey: 'priorart' },
    { key: 'sep', separator: true },
    { key: 'qa',           label: 'Q&A',              path: '/demo', tabKey: 'qa' },
    { key: 'chat',         label: 'Chat',             path: '/chat' },
    { key: 'priorart_web', label: 'Prior Art',        path: '/demo', tabKey: 'priorart_web' },
    { key: 'discrep',      label: 'Discrepancy',      path: '/demo', tabKey: 'discrep' },
    { key: 'portfolios',   label: 'Portfolios',       path: '/portfolios' },
    ...(isAdmin ? [
      { key: 'sep2', separator: true },
      { key: 'admin',       label: 'Activity Log', path: '/admin' },
      { key: 'admin-users', label: 'Users',         path: '/admin/users' },
    ] : []),
  ];

  const sidebar = (
    <nav className="app-sidebar">
      <img src={gavilLogo} alt="Gavil" className="sidebar-logo" onClick={() => navigate('/')} />
      <ul className="sidebar-nav">
        {sidebarNavItems.map(({ key, label, path, tabKey, separator }) => {
          if (separator) return <li key={key} className="sidebar-nav-sep" />;
          const active = location.pathname === path && (!tabKey || tab === tabKey);
          return (
            <li
              key={key}
              className={`sidebar-nav-item${active ? ' sidebar-nav-item-active' : ''}`}
              onClick={() => { navigate(path); if (tabKey) setTab(tabKey); }}
            >
              {label}
            </li>
          );
        })}
      </ul>
      <div className="sidebar-footer">
        {userCredits !== null && (
          <div className="sidebar-credits">{userCredits.toLocaleString()} credits</div>
        )}
        <div ref={userMenuRef} style={{ position: 'relative' }}>
          <div className="sidebar-avatar" onClick={() => setUserMenuOpen(o => !o)}>
            {userInitials}
          </div>
          {userMenuOpen && (
            <div className="sidebar-user-popup" onMouseLeave={() => setUserMenuOpen(false)}>
              <div style={{ padding: '10px 16px', fontSize: 12, color: '#888', borderBottom: '1px solid #f0f0f0' }}>
                {user?.email}
              </div>
              <div
                className="sidebar-menu-item"
                onClick={() => { alert('To add more credits, please contact us at admin@gavil.ai.'); setUserMenuOpen(false); }}
              >
                Add more credits
              </div>
              <div
                className="sidebar-menu-item"
                style={{ borderTop: '1px solid #f0f0f0' }}
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Sign out
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );

  const appShell = (content) => (
    <div className="shell">
      {sidebar}
      <main className="shell-main">
        {content}
      </main>
    </div>
  );

  const tabContent = (
    <>
      {tab === 'qa' && (
        <div className="tab-content">
          <div className="tab-index-row">
            <label className="priorart-label">Source:</label>
            <select
              className="index-select"
              value={selectedPortfolioId ? `p:${selectedPortfolioId}` : selectedIndex}
              onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith('p:')) setSelectedPortfolioId(v.slice(2));
                else { setSelectedPortfolioId(''); setSelectedIndex(v); }
              }}
            >
              {indexList.filter((name) => !name.startsWith('patent')).map((name) => (
                <option key={name} value={name}>{indexDisplayNames[name] || name}</option>
              ))}
              {portfolios.length > 0 && <option disabled>──── Portfolios ────</option>}
              {portfolios.map((p) => (
                <option key={`p:${p.id}`} value={`p:${p.id}`}>{p.name}</option>
              ))}
            </select>
          </div>
          <form onSubmit={handleSubmit} className="search-form">
            <select
              className="index-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={loading}
            >
              {modelOptions.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask a question..."
              disabled={loading}
            />
            <button type="submit" disabled={loading || !query.trim()}>
              {loading ? 'Searching...' : 'Ask'}
            </button>
          </form>

          {error && <div className="error">{error}</div>}

          {answer && (
            <div className="results">
              <div className="answer-box">
                <h2>Answer</h2>
                {answer.answer ? (
                  <div className="answer-text">{renderCitations(answer.answer, sourcesByNumber, handleSourceClick)}</div>
                ) : (
                  <div className="answer-spinner-container">
                    <div className="pdf-page-spinner" />
                  </div>
                )}
              </div>

              {answer.sources && answer.sources.length > 0 && (
                <div className="sources">
                  <h3>Sources</h3>
                  <ul>
                    {answer.sources.map((s) => (
                      <li
                        key={s.number}
                        className={s.filename && s.bboxes && s.bboxes.length > 0 ? 'source-clickable' : ''}
                        onClick={() => handleSourceClick(s)}
                      >
                        <span className="source-number">[{s.number}]</span>
                        <div className="source-text">
                          <span className="source-summary">{s.summary}</span>
                          <span className="source-title">{s.title}...</span>
                        </div>
                        <div className="source-scores">
                          {s.rerank_score != null && (
                            <span className="source-score">{s.rerank_score.toFixed(3)}</span>
                          )}
                          <span className="source-score">{s.score.toFixed(3)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'chat' && (
        <div className="tab-content chat-tab">
          <div className="tab-index-row">
            <label className="priorart-label">Source:</label>
            <select
              className="index-select"
              value={selectedPortfolioId ? `p:${selectedPortfolioId}` : selectedIndex}
              onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith('p:')) setSelectedPortfolioId(v.slice(2));
                else { setSelectedPortfolioId(''); setSelectedIndex(v); }
              }}
            >
              {indexList.filter((name) => !name.startsWith('patent')).map((name) => (
                <option key={name} value={name}>{indexDisplayNames[name] || name}</option>
              ))}
              {portfolios.length > 0 && <option disabled>──── Portfolios ────</option>}
              {portfolios.map((p) => (
                <option key={`p:${p.id}`} value={`p:${p.id}`}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="chat-messages">
            {chatMessages.length === 0 && (
              <div className="chat-empty">Start a conversation by typing a message below.</div>
            )}
            {chatMessages.map((msg, i) => {
              if (msg.role === 'assistant' && msg.sources && msg.sources.length > 0) {
                const srcMap = {};
                for (const s of msg.sources) srcMap[s.number] = s;
                return (
                  <div key={i} className={`chat-bubble chat-bubble-${msg.role}`}>
                    {renderCitations(msg.content, srcMap, handleSourceClick)}
                  </div>
                );
              }
              return (
                <div key={i} className={`chat-bubble chat-bubble-${msg.role}`}>
                  {msg.content}
                </div>
              );
            })}
            {chatLoading && (
              <div className="answer-spinner-container">
                <div className="pdf-page-spinner" />
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={handleChatSubmit} className="chat-input-form">
            <select
              className="index-select"
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              disabled={chatLoading}
            >
              {REASONING_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Type a message..."
              disabled={chatLoading}
            />
            <button type="submit" disabled={chatLoading || !chatInput.trim()}>
              {chatLoading ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      )}

      {tab === 'discrep' && (
        <div className="tab-content discrep-tab">
          <div className="tab-index-row">
            <label className="priorart-label">Source:</label>
            <select
              className="index-select"
              value={selectedPortfolioId ? `p:${selectedPortfolioId}` : selectedIndex}
              onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith('p:')) setSelectedPortfolioId(v.slice(2));
                else { setSelectedPortfolioId(''); setSelectedIndex(v); }
              }}
            >
              {indexList.filter((name) => !name.startsWith('patent')).map((name) => (
                <option key={name} value={name}>{indexDisplayNames[name] || name}</option>
              ))}
              {portfolios.length > 0 && <option disabled>──── Portfolios ────</option>}
              {portfolios.map((p) => (
                <option key={`p:${p.id}`} value={`p:${p.id}`}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="discrep-panels">
            <div className="discrep-left">
              <form onSubmit={handleDiscrepSubmit} className="discrep-form">
                <select
                  className="index-select"
                  value={discrepModel}
                  onChange={(e) => setDiscrepModel(e.target.value)}
                  disabled={discrepLoading}
                  style={showLlmChoice ? undefined : { display: 'none' }}
                >
                  {modelOptions.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="discrep-title"
                  value={discrepTitle}
                  onChange={(e) => setDiscrepTitle(e.target.value)}
                  placeholder="Title / context for the text below..."
                  disabled={discrepLoading}
                />
                <textarea
                  className="discrep-textarea"
                  value={discrepInput}
                  onChange={(e) => setDiscrepInput(e.target.value)}
                  placeholder="Paste text to check for discrepancies..."
                  disabled={discrepLoading}
                />
                <button type="submit" disabled={discrepLoading || !discrepInput.trim()}>
                  {discrepLoading ? 'Analyzing...' : 'Submit'}
                </button>
              </form>
            </div>
            <div className="discrep-right">
              {discrepError && <div className="error">{discrepError}</div>}
              {discrepLoading && discrepItems.length === 0 && (
                <div className="answer-spinner-container">
                  <div className="pdf-page-spinner" />
                </div>
              )}
              {discrepItems.length === 0 && !discrepLoading && !discrepError && !discrepSubmitted && (
                <div className="chat-empty">Results will appear here.</div>
              )}
              {discrepItems.length === 0 && !discrepLoading && !discrepError && discrepSubmitted && (
                <div className="chat-empty">No discrepancies found.</div>
              )}
              {sortedDiscrepItems.length > 0 && sortedDiscrepItems.map((d, i) => (
                <div key={i} className="discrep-item">
                  <div className="discrep-item-header">
                    <span className="discrep-topic">{d.topic}</span>
                    <span className={`discrep-severity discrep-severity-${d.severity >= 7 ? 'high' : d.severity >= 4 ? 'med' : 'low'}`}>
                      {d.severity}/10
                    </span>
                  </div>
                  <div className="discrep-explanation">
                    {renderCitations(d.explanation, discrepSourcesByNumber, handleSourceClick)}
                  </div>
                  {d.evidence && Array.isArray(d.evidence) && d.evidence.length > 0 && (
                    <ul className="discrep-evidence-list">
                      {d.evidence.map((ev, j) => {
                        const src = discrepSourcesByNumber[ev.citation];
                        const clickable = src && src.filename && src.bboxes && src.bboxes.length > 0;
                        const evEmphasis = ev.bboxes && ev.bboxes.length > 0 ? ev.bboxes : null;
                        return (
                          <li key={j} className="discrep-evidence-item">
                            <span className="discrep-evidence-text">"{ev.text}"</span>
                            {ev.citation && (
                              <span
                                className={clickable ? 'citation-link' : 'discrep-evidence-cite'}
                                onClick={clickable ? () => handleSourceClick(src, { showSummary: true, emphasisBboxes: evEmphasis }) : undefined}
                              >
                                [{ev.citation}]
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'priorart' && (
        <div className="tab-content priorart-tab">
          <div className="priorart-controls">
            <div className="priorart-index-row" style={showLlmChoice ? undefined : { display: 'none' }}>
              <label className="priorart-label">Model</label>
              <select
                className="index-select"
                value={priorArtModel}
                onChange={(e) => setPriorArtModel(e.target.value)}
                disabled={priorArtLoading}
              >
                {modelOptions.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <PatentUploadPicker label="Patent" upload={priorArtUpload2} disabled={priorArtLoading || !defaultPortfolioId} portfolios={portfolios} portfolioDocsMap={portfolioDocsMap} />
            {priorArtSlotIds.map((slotId, i) => (
              <PriorArtSlot
                key={slotId}
                slotId={slotId}
                label={i === 0 ? 'Prior Art' : ''}
                portfolioId={defaultPortfolioId}
                authFetch={authFetch}
                disabled={priorArtLoading || !defaultPortfolioId}
                portfolios={portfolios}
                portfolioDocsMap={portfolioDocsMap}
                onDocChange={handlePriorArtSlotDocChange}
                onRemove={removePriorArtSlot}
                removable={priorArtSlotIds.length > 1}
              />
            ))}
            <div className="priorart-index-row" style={{ marginBottom: 24 }}>
              <label className="priorart-label" />
              <button
                type="button"
                className="portfolio-browse-btn"
                onClick={addPriorArtSlot}
                disabled={priorArtLoading || priorArtSlotIds.length >= PRIOR_ART_MAX_SLOTS}
                title="Add another prior-art reference (e.g. for an obviousness combination)"
              >
                + Add another reference
              </button>
            </div>
            <div className="priorart-index-row" style={{ marginBottom: 24 }}>
              <label className="priorart-label">Perspective</label>
              <select
                className="index-select"
                value={priorArtUseCase}
                onChange={(e) => {
                  // Switching Validity/Invalidity/Neutral changes what a regenerated
                  // chart would actually say (the LLM posture), so the currently-shown
                  // results -- generated under the OLD posture -- would otherwise sit
                  // on screen looking like they match the new dropdown selection when
                  // they don't. Clear them so nothing stale is displayed until the user
                  // clicks Generate again.
                  setPriorArtUseCase(e.target.value);
                  setPriorArtResults([]);
                  setPriorArtError(null);
                  setPriorArtTotal(0);
                }}
                disabled={priorArtLoading}
                title="Which side's argument the claim chart should build"
              >
                <option value="plaintiff">Validity</option>
                <option value="defense">Invalidity</option>
                <option value="examiner">Neutral</option>
              </select>
            </div>
            {priorArtClaimNumbers.length > 0 && (
              <div className="priorart-index-row" style={{ marginBottom: 24 }}>
                <label className="priorart-label">Claims</label>
                <div className="priorart-claim-toggles">
                  {priorArtClaimNumbers.map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`priorart-claim-toggle${priorArtSelectedClaims.has(n) ? ' on' : ''}`}
                      onClick={() => togglePriorArtClaim(n)}
                      disabled={priorArtLoading}
                      title={`Claim ${n} — click to ${priorArtSelectedClaims.has(n) ? 'exclude from' : 'include in'} the next Generate`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="priorart-index-row">
              <button className="priorart-check-btn" onClick={handlePriorArtCheck} disabled={priorArtLoading || priorArtIndexList.length === 0 || !priorArtIndex2 || priorArtSelectedClaims.size === 0}>
                {priorArtLoading ? 'Generating...' : 'Generate'}
              </button>
              {priorArtLoading && (
                <div className="suff-progress-wrap" style={{width:'50%', marginLeft: 16}}>
                  <div className="suff-progress-track">
                    {priorArtTotal === 0
                      ? <div className="suff-progress-scan" />
                      : <div className="suff-progress-fill"
                             style={{width: `${Math.round(priorArtResults.length / priorArtTotal * 100)}%`}} />
                    }
                  </div>
                  <span className="suff-progress-label">
                    {priorArtTotal === 0
                      ? 'Searching…'
                      : `${priorArtResults.length} / ${priorArtTotal}`}
                  </span>
                </div>
              )}
              {priorArtResults.length > 0 && !priorArtLoading && (
                <>
                  <button className="suff-export-btn" onClick={handleExportClaimChartPdf}>
                    Export to PDF
                  </button>
                  <button className="suff-export-btn" onClick={handleExportClaimChartDocx} style={{ marginLeft: 8 }}>
                    Export to Word
                  </button>
                </>
              )}
            </div>
          </div>
          {priorArtError && <div className="error">{priorArtError}</div>}
          <div className="priorart-results">
            {priorArtResults.length === 0 && !priorArtLoading && !priorArtError && (
              <div className="chat-empty">Select two patents and click Generate.</div>
            )}
            {priorArtResults.length > 0 && (
              <div className="claim-chart-disclaimer">
                <div className="claim-chart-disclaimer-title">{CLAIM_CHART_DISCLAIMER.title}</div>
                {CLAIM_CHART_DISCLAIMER.sections.map((sec) => (
                  <div key={sec.heading} className="claim-chart-disclaimer-section">
                    <div className="claim-chart-disclaimer-heading">{sec.heading}</div>
                    {sec.paragraphs.map((para, i) => <p key={i}>{para}</p>)}
                  </div>
                ))}
                <div className="claim-chart-disclaimer-footer">{CLAIM_CHART_DISCLAIMER.footer}</div>
              </div>
            )}
            {priorArtResults.length > 0 && claimChartColumns(priorArtUseCase).leadIn && (
              <p className="claim-chart-lead-in">{claimChartColumns(priorArtUseCase).leadIn}</p>
            )}
            {priorArtResults.length > 0 && (
              <table className={`claim-chart-table${claimChartColumns(priorArtUseCase).rebuttal ? ' claim-chart-4col' : ''}`}>
                <thead>
                  <tr>
                    <th>Claim</th>
                    <th>Limitation</th>
                    <th>{claimChartColumns(priorArtUseCase).argument}</th>
                    {claimChartColumns(priorArtUseCase).rebuttal && <th>{claimChartColumns(priorArtUseCase).rebuttal}</th>}
                  </tr>
                </thead>
                <tbody>
                  {priorArtResults.map((r) => {
                    const lims = r.limitations.length > 0 ? r.limitations : [{ limitation: '', summary: '', references: [] }];
                    return lims.map((lim, li) => (
                      <tr key={`${r.claim_id}-${li}`}>
                        {li === 0 && (
                          <td className="claim-chart-claim" rowSpan={lims.length}>
                            {(() => {
                              const cs = r.claim_source;
                              const limClickable = cs && cs.filename && cs.bboxes && cs.bboxes.length > 0;
                              const limEmphasis = lim.emphasis_bboxes && lim.emphasis_bboxes.length > 0 ? lim.emphasis_bboxes : null;
                              return limClickable ? (
                                <span
                                  className="citation-link"
                                  onClick={() => handleSourceClick(cs, { showSummary: false, emphasisBboxes: limEmphasis })}
                                >
                                  Claim {r.claim_number}
                                </span>
                              ) : (
                                <span>Claim {r.claim_number}</span>
                              );
                            })()}
                            {r.verdict && (
                              <div className="claim-chart-verdict">
                                <span className={`suff-badge suff-${r.verdict.tier}`}>{r.verdict.label}</span>
                              </div>
                            )}
                          </td>
                        )}
                        <td className="claim-chart-limitation">
                          {lim.score != null && (
                            <div className="suff-header">
                              <span className={`suff-score suff-${scoreTier(lim.score)}`}>{lim.score}/10</span>
                            </div>
                          )}
                          {renderHighlighted(lim.limitation, computeHighlightSpans(
                            lim.limitation, lim.term_highlights, (g) => g.limitation_phrase,
                            (g, gi) => groupHasReferenceMatch(g) ? TERM_HIGHLIGHT_COLORS[gi % TERM_HIGHLIGHT_COLORS.length] : SUMMARY_HIGHLIGHT_COLOR
                          ))}
                        </td>
                        <td className="claim-chart-analysis">
                          {lim.score == null && !lim._inherited && !(lim.summary && lim.summary.trim()) && (
                            // A purely organizational clause (e.g. "wherein X comprises:") that
                            // only reintroduces an already-claimed element -- nothing of its own
                            // to score, since that's fully captured by the sub-element rows below.
                            // Reuses the same meta-note style as "See, e.g:" so it reads as an
                            // intentional pointer, not a blank/skipped row. A "see_claim" pointer
                            // row (a dependent claim's own preamble, e.g. "A method as set forth
                            // in claim 1 including the step of") instead falls through to the
                            // normal rendering below, since the server fills its summary with a
                            // plain "See claim N." (see _see_claim_disposition).
                            <div className="claim-chart-see">See below</div>
                          )}
                          {renderClaimChartBody(lim, lim, true)}
                        </td>
                        {claimChartColumns(priorArtUseCase).rebuttal && (
                          <td className="claim-chart-analysis claim-chart-rebuttal">
                            {lim.rebuttal
                              ? renderClaimChartBody(lim.rebuttal, lim, false)
                              : <div className="claim-chart-see">{rebuttalPlaceholder(lim)}</div>}
                          </td>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            )}
            {priorArtLoading && (
              <div className="answer-spinner-container">
                <div className="pdf-page-spinner" />
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'priorart_web' && !isAdmin && (
        <div className="tab-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 12, color: '#888' }}>
          <div style={{ fontSize: 32 }}>🚧</div>
          <div style={{ fontSize: 18, fontWeight: 500 }}>This feature is under construction.</div>
        </div>
      )}

      {tab === 'priorart_web' && isAdmin && (
        <div className="tab-content priorart-tab">
          <div className="priorart-controls">
            <div className="priorart-index-row">
              <label className="priorart-label">Portfolio</label>
              <select
                className="index-select"
                value={patentPortfolioId}
                onChange={(e) => { setPatentPortfolioId(e.target.value); setPaWebIndex(''); setPaWebClaims([]); }}
                disabled={paWebLoading}
              >
                <option value="">— select portfolio —</option>
                {portfolios.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="priorart-index-row">
              <label className="priorart-label">Under Suit</label>
              <select
                className="index-select"
                value={paWebIndex}
                onChange={(e) => setPaWebIndex(e.target.value)}
                disabled={paWebLoading}
              >
                <option value="">— select patent —</option>
                {patentDocs.map((d) => (
                  <option key={d.id} value={d.id}>{d.display_name || d.name}</option>
                ))}
              </select>
            </div>
            <div className="priorart-index-row">
              <label className="priorart-label">Filing Date</label>
              {paWebDateLoading ? (
                <span style={{ fontSize: 13, color: '#888' }}>Looking up…</span>
              ) : (
                <input
                  type="date"
                  className="index-select"
                  value={paWebFilingDate}
                  onChange={(e) => setPaWebFilingDate(e.target.value)}
                  disabled={paWebLoading}
                />
              )}
            </div>
          </div>
          {paWebError && <div className="error">{paWebError}</div>}
          <div className="priorart-results">
            {paWebLoading && (
              <div className="answer-spinner-container"><div className="pdf-page-spinner" /></div>
            )}
            {!paWebLoading && paWebClaims.length === 0 && !paWebError && (
              <div className="chat-empty">Select a patent to load its claims.</div>
            )}
            {paWebClaims.length > 0 && (
              <table className="claim-chart-table">
                <thead>
                  <tr>
                    <th>Claim</th>
                    <th>Limitation</th>
                    <th>Prior Art Analysis</th>
                  </tr>
                </thead>
                <tbody>
                  {paWebClaims.map((claim) => {
                    if (claim.status === 'idle') {
                      return (
                        <tr key={claim.claim_id} className="source-clickable" onClick={() => handlePaWebClaimClick(claim)}>
                          <td className="claim-chart-claim">Claim {claim.claim_number}</td>
                          <td className="claim-chart-limitation" colSpan={2}>
                            {claim.claim_text}
                          </td>
                        </tr>
                      );
                    }
                    if (claim.status === 'loading') {
                      return (
                        <tr key={claim.claim_id}>
                          <td className="claim-chart-claim">Claim {claim.claim_number}</td>
                          <td colSpan={2}><div className="pdf-page-spinner" style={{ margin: '8px auto' }} /></td>
                        </tr>
                      );
                    }
                    if (claim.status === 'error') {
                      return (
                        <tr key={claim.claim_id}>
                          <td className="claim-chart-claim">Claim {claim.claim_number}</td>
                          <td colSpan={2} className="error">{claim.error}</td>
                        </tr>
                      );
                    }
                    // done — expand into one row per limitation
                    const lims = claim.limitations.length > 0
                      ? claim.limitations
                      : [{ limitation: '(no limitations found)', references: [] }];
                    return lims.map((lim, li) => (
                      <tr key={`${claim.claim_id}-${li}`}>
                        {li === 0 && (
                          <td className="claim-chart-claim" rowSpan={lims.length}>
                            Claim {claim.claim_number}
                          </td>
                        )}
                        <td className="claim-chart-limitation">{lim.limitation}</td>
                        <td className="claim-chart-analysis">
                          {(lim.references || []).map((ref, j) => (
                            <div key={j} className="claim-chart-ref">
                              <div className="claim-chart-ref-title">
                                {ref.url ? (
                                  <a href={ref.url.startsWith('http') ? ref.url : `https://patents.google.com/patent/${ref.url}`} target="_blank" rel="noopener noreferrer">{ref.title || ref.url}</a>
                                ) : (
                                  ref.title
                                )}
                                {ref.patent_number && <span style={{ color: '#555', marginLeft: 6, fontWeight: 500 }}>{ref.patent_number}</span>}
                                {ref.assignee && <span style={{ color: '#666', marginLeft: 6 }}>({ref.assignee})</span>}
                                {ref.date && <span style={{ color: '#888', marginLeft: 6 }}>({ref.date})</span>}
                              </div>
                              <div className="claim-chart-ref-text">
                                {ref.confidence != null && (
                                  <span style={{
                                    display: 'inline-block', marginRight: 8, fontWeight: 600,
                                    color: ref.confidence >= 7 ? '#1a7f37' : ref.confidence >= 4 ? '#b08800' : '#c0392b'
                                  }}>{ref.confidence}/10</span>
                                )}
                                {ref.analysis}
                              </div>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'sufficiency' && (
        <div className="tab-content priorart-tab">
          {suffInfoPopup && (
            <div className="suff-info-overlay" onClick={() => setSuffInfoPopup(null)}>
              <div className="suff-info-modal" onClick={(e) => e.stopPropagation()}>
                <button className="suff-info-close" onClick={() => setSuffInfoPopup(null)}>✕</button>
                {suffInfoPopup === 'written_description' && (<>
                  <h3>Written Description</h3>
                  <p>Under 35 U.S.C. § 112(a), the specification must demonstrate that the inventor actually possessed the full scope of the claimed invention at the time of filing. The question is whether the disclosure reasonably conveys to a skilled reader that the inventor had possession of what is claimed.</p>
                  <p>Common failure modes: claims that use language absent from the specification; claims that cover a broad genus when the spec discloses only narrow species; claims whose scope was expanded during prosecution without corresponding spec support.</p>
                </>)}
                {suffInfoPopup === 'enablement' && (<>
                  <h3>Enablement</h3>
                  <p>Under 35 U.S.C. § 112(a), the specification must enable a person of ordinary skill in the art (PHOSITA) to make and use the full scope of the claimed invention without undue experimentation. Breadth of the claim is weighed against the guidance provided.</p>
                  <p>Key factors (Wands): quantity of experimentation required, amount of guidance in the spec, presence of working examples, predictability of the art, and breadth of the claims relative to the disclosure. Functional genus claims (e.g., "any antibody that achieves X") are especially vulnerable when membership in the genus can only be determined by trial-and-error screening.</p>
                </>)}
                {suffInfoPopup === 'indefiniteness' && (<>
                  <h3>Indefiniteness</h3>
                  <p>Under 35 U.S.C. § 112(b), a claim must particularly point out and distinctly claim the invention. A claim is indefinite if it fails to inform a skilled reader, read in light of the specification, of the scope of the invention with reasonable certainty.</p>
                  <p>Common failure modes: purely relative or subjective terms (e.g., "high," "sufficient," "optimal") with no objective anchor in the specification; purely functional language with no structural anchor; means-plus-function limitations without corresponding structure in the spec; claim terms that contradict or are absent from the specification.</p>
                </>)}
              </div>
            </div>
          )}
          <div className="priorart-controls">
            <PatentUploadPicker label="Patent" upload={{ ...suffUpload, handleFileSelect: handleSuffFileSelect, selectExisting: handleSuffSelectExisting }} disabled={suffLoading || !defaultPortfolioId} portfolios={portfolios} portfolioDocsMap={portfolioDocsMap} />
            <div className="priorart-index-row" style={{ marginBottom: 24 }}>
              <label className="priorart-label">Analysis</label>
              <select
                className="index-select"
                value={suffUseCase}
                onChange={(e) => setSuffUseCase(e.target.value)}
                disabled={suffLoading}
                title="Which side's argument the PHOSITA definition should build"
              >
                <option value="plaintiff">Validity</option>
                <option value="defense">Invalidity</option>
                <option value="examiner">Neutral</option>
              </select>
            </div>
            <div className="suff-phosita-row" style={suffProcessing ? { opacity: 0.5 } : undefined}>
              <label className="priorart-label">PHOSITA<br/>Definition</label>
              <textarea
                className="suff-phosita-textarea"
                value={suffPhositaLoading ? 'Inferring PHOSITA definition...' : suffPhosita}
                onChange={(e) => setSuffPhosita(e.target.value)}
                disabled={suffProcessing || suffPhositaLoading || suffLoading}
                rows={6}
                placeholder="PHOSITA definition will be inferred when a patent is selected."
              />
            </div>
            <div className="priorart-index-row">
              {suffClaimNumbers.length > 0 && (
                <select
                  className="index-select"
                  value={suffMaxClaim}
                  onChange={(e) => setSuffMaxClaim(Number(e.target.value))}
                  disabled={suffLoading}
                  title="Analyze claims 1 through N"
                >
                  {suffClaimNumbers.map((n, i) => (
                    <option key={n} value={n}>
                      {i === suffClaimNumbers.length - 1 ? 'All Claims' : `Claims 1–${n}`}
                    </option>
                  ))}
                </select>
              )}
              <button
                className="priorart-check-btn"
                onClick={handleSufficiencyCheck}
                disabled={suffLoading || !suffIndex || suffPhositaLoading}
              >
                {suffLoading ? 'Analyzing...' : 'Run Check'}
              </button>
              {suffLoading && (
                <div className="suff-progress-wrap" style={{width:'50%', marginLeft: 16}}>
                  <div className="suff-progress-track">
                    {suffTotal === 0
                      ? <div className="suff-progress-scan" />
                      : <div className="suff-progress-fill"
                             style={{width: `${Math.round(suffLimitations.length / suffTotal * 100)}%`}} />
                    }
                  </div>
                  <span className="suff-progress-label">
                    {suffTotal === 0
                      ? 'Searching\u2026'
                      : `${suffLimitations.length} / ${suffTotal}`}
                  </span>
                </div>
              )}
              {suffLimitations.length > 0 && !suffLoading && (
                <button className="suff-export-btn" onClick={handleExportSufficiencyPdf}>
                  Export to PDF
                </button>
              )}
            </div>
          </div>
          {suffError && <div className="error">{suffError}</div>}
          <div className="priorart-results">
            {suffLimitations.length === 0 && !suffLoading && !suffError && (
              <div className="chat-empty">
                {suffProcessing
                  ? 'Processing your patent — this can take a minute…'
                  : 'Upload a patent PDF and click Run Check.'}
              </div>
            )}
            {suffLimitations.length > 0 && (
              <table className="claim-chart-table" style={{tableLayout: 'fixed', width: '100%'}}>
                <colgroup>
                  <col style={{width: '7%'}} />
                  <col style={{width: '20%'}} />
                  <col style={{width: '73%'}} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Claim</th>
                    <th>Limitation</th>
                    <th>
                      § 112 Analysis
                      <span className="suff-header-info">
                        {[
                          { key: 'written_description', label: 'Written Desc.' },
                          { key: 'enablement', label: 'Enablement' },
                          { key: 'indefiniteness', label: 'Indefiniteness' },
                        ].map(({ key, label }) => (
                          <button key={key} className="suff-info-btn" onClick={() => setSuffInfoPopup(key)}>{label} ⓘ</button>
                        ))}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const DOCTRINE_ABBR = { written_description: 'Written Desc.', enablement: 'Enablement', indefiniteness: 'Indefiniteness' };
                    const colorClass = (s) => s === 'adequate' ? 'suff-adequate' : s === 'deficient' ? 'suff-deficient' : 'suff-concern';

                    // Render {N} source citations as clickable links
                    const renderCitations = (rawText, sources) => {
                      let text;
                      if (rawText == null) text = '';
                      else if (typeof rawText === 'string') text = rawText;
                      // Model returned an object instead of a string; extract the key (which is the text)
                      else if (typeof rawText === 'object') text = String(Object.keys(rawText)[0] || Object.values(rawText)[0] || '');
                      else text = String(rawText);
                      if (!text || !sources || sources.length === 0) return text;
                      const parts = [];
                      let last = 0;
                      const re = /\{(\d+)\}/g;
                      let m;
                      while ((m = re.exec(text)) !== null) {
                        const n = parseInt(m[1], 10);
                        const src = sources[n - 1];
                        parts.push(text.slice(last, m.index));
                        if (src && src.filename && src.bboxes && src.bboxes.length > 0) {
                          parts.push(
                            <span key={m.index} className="citation-link"
                              onClick={() => handleSourceClick(src)}>
                              {src.label || ('{' + n + '}')}
                            </span>
                          );
                        } else {
                          parts.push(m[0]);
                        }
                        last = m.index + m[0].length;
                      }
                      parts.push(text.slice(last));
                      return parts;
                    };

                    const renderAnalysisCell = (statusRaw, score, doctrines, analysis, issues, precedent, sources) => {
                      const status = (statusRaw || 'concern').toLowerCase();
                      const cc = colorClass(status);
                      return (
                        <td className="claim-chart-analysis">
                          <div className="suff-header">
                            <span className={`suff-badge ${cc}`}>{status || 'concern'}</span>
                            {score != null && <span className={`suff-score ${cc}`}>{score}/10</span>}
                            {doctrines && (
                              <span className="suff-doctrine-badges">
                                {Object.entries(DOCTRINE_ABBR).map(([k, abbr]) => (
                                  <span key={k} className={`suff-doctrine-badge ${colorClass(doctrines[k] || 'concern')}`}
                                    title={k.replace(/_/g, ' ')}>
                                    {abbr}
                                  </span>
                                ))}
                              </span>
                            )}
                          </div>
                          <div className="suff-analysis">{renderCitations(analysis, sources)}</div>
                          {issues && issues.length > 0 && (
                            <ul className="suff-issues">
                              {issues.map((issue, k) => <li key={k}>{renderCitations(issue, sources)}</li>)}
                            </ul>
                          )}
                          {(status === 'concern' || status === 'deficient') && precedent && (
                            <div className="suff-precedent">
                              <div className="suff-precedent-header">Precedent</div>
                              {status === 'deficient' ? (
                                <ul className="suff-issues">
                                  {(precedent.for_invalidity || []).map((c, k) => <li key={k}>{c}</li>)}
                                </ul>
                              ) : (
                                <div className="suff-precedent-cols">
                                  <div className="suff-precedent-col">
                                    <div className="suff-precedent-side suff-deficient-text">Supporting deficiency</div>
                                    <ul className="suff-issues">
                                      {(precedent.for_invalidity || []).map((c, k) => <li key={k}>{c}</li>)}
                                    </ul>
                                  </div>
                                  <div className="suff-precedent-col">
                                    <div className="suff-precedent-side suff-adequate-text">Supporting adequacy</div>
                                    <ul className="suff-issues">
                                      {(precedent.for_validity || []).map((c, k) => <li key={k}>{c}</li>)}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    };

                    const rows = [];
                    let i = 0;
                    while (i < suffLimitations.length) {
                      const r = suffLimitations[i];
                      let j = i + 1;
                      while (j < suffLimitations.length && suffLimitations[j].claim_number === r.claim_number) j++;
                      const claimLims = suffLimitations.slice(i, j);
                      const claimRowSpan = claimLims.length + (suffClaimLevel[r.claim_number] ? 1 : 0);
                      claimLims.forEach((lim, li) => {
                        const cs = lim.claim_source || {};
                        const claimClickable = cs.filename && cs.bboxes && cs.bboxes.length > 0;
                        rows.push(
                          <tr key={`${lim.claim_id}-${lim.limitation_index}`}>
                            {li === 0 && (
                              <td className="claim-chart-claim" rowSpan={claimRowSpan}>
                                {claimClickable ? (
                                  <span className="citation-link" onClick={() => handleSourceClick(cs, { showSummary: false, suppressHighlight: true })}>
                                    Claim {lim.claim_number}
                                  </span>
                                ) : (
                                  <span>Claim {lim.claim_number}</span>
                                )}
                              </td>
                            )}
                            <td className="claim-chart-limitation">
                              {lim.limitation}
                              {(lim.evidence || []).map((ev, evIdx) => {
                                const evSource = lim.spec_sources ? lim.spec_sources[ev.citation - 1] : null;
                                const evClickable = evSource && evSource.filename && evSource.bboxes && evSource.bboxes.length > 0;
                                return evClickable ? (
                                  <span
                                    key={evIdx}
                                    className="citation-link claim-chart-cite"
                                    title="View supporting specification text"
                                    onClick={() => handleSourceClick(evSource, { showSummary: true, emphasisBboxes: ev.bboxes, suppressHighlight: true })}
                                  >
                                    {ev.label || `[${ev.citation}]`}
                                  </span>
                                ) : null;
                              })}
                            </td>
                            {renderAnalysisCell(lim.status, lim.score, lim.doctrines, lim.analysis, lim.issues, lim.precedent, lim.spec_sources)}
                          </tr>
                        );
                      });
                      // Claim-level row
                      const cl = suffClaimLevel[r.claim_number];
                      if (cl) {
                        rows.push(
                          <tr key={`cl-${r.claim_number}`} className="suff-claim-level-row">
                            <td className="claim-chart-limitation suff-claim-level-label">
                              Claim scope vs. embodiments
                            </td>
                            {renderAnalysisCell(cl.status, cl.score, null, cl.analysis, cl.issues, null, cl.spec_sources)}
                          </tr>
                        );
                      }
                      i = j;
                    }
                    return rows;
                  })()}
                </tbody>
              </table>
            )}
            {suffLoading && (
              <div className="answer-spinner-container">
                <div className="pdf-page-spinner" />
              </div>
            )}
          </div>
        </div>
      )}

      {pdfModal && (
        <div className="pdf-modal-overlay" onClick={closeModal}>
          <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <span className="pdf-modal-title">{pdfModal.title}</span>
              <button className="pdf-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="pdf-modal-info">
              <span className="pdf-modal-page">Page {currentPage}{pdfDoc ? ` / ${pdfDoc.numPages}` : ''}</span>
              <div className="pdf-search-box">
                <input
                  className="pdf-search-input"
                  type="text"
                  placeholder="Search…"
                  value={pdfSearchTerm}
                  onChange={(e) => handlePdfSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigatePdfSearch(e.shiftKey ? -1 : 1);
                  }}
                />
                {pdfSearchResults.length > 0 && (
                  <>
                    <span className="pdf-search-count">
                      {pdfSearchIdx + 1}/{pdfSearchResults.length}
                    </span>
                    <button className="pdf-search-nav" onClick={() => navigatePdfSearch(-1)}>‹</button>
                    <button className="pdf-search-nav" onClick={() => navigatePdfSearch(1)}>›</button>
                  </>
                )}
                {pdfSearchTerm && pdfSearchResults.length === 0 && (
                  <span className="pdf-search-count">0 results</span>
                )}
              </div>
              <button
                className="pdf-modal-summary-btn"
                onClick={() => {
                  setShowSummary((v) => !v);
                  const el = scrollRef.current?.querySelector(
                    `[data-page="${firstHighlightPage}"]`,
                  );
                  if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
                }}
              >
                Summary
              </button>
            </div>
            <div className="pdf-modal-body-wrap">
              {!renderedPages.has(currentPage) && (
                <div className="pdf-modal-spinner-overlay">
                  <div className="pdf-page-spinner" />
                </div>
              )}
              <div className="pdf-modal-body" ref={scrollRef} onScroll={handleScroll} onClick={() => setShowSummary(false)}>
                {pdfDoc && pageNumbers.map((pageNum) => (
                  <PdfPage
                    key={pageNum}
                    pdf={pdfDoc}
                    pageNum={pageNum}
                    scale={PDF_SCALE}
                    highlights={bboxesByPage[pageNum] || NO_HIGHLIGHTS}
                    emphasisHighlights={emphasisBboxesByPage[pageNum] || NO_HIGHLIGHTS}
                    searchHighlights={searchHighlightsByPage[pageNum] || NO_HIGHLIGHTS}
                    highlightColor={pdfModal?.highlightColor}
                    coloredHighlights={pdfModal?.filename ? (
                      (preciseSuffHighlights[pdfModal.filename] ?? suffColoredHighlightsByPage[pdfModal.filename] ?? {})[pageNum] || NO_HIGHLIGHTS
                    ) : NO_HIGHLIGHTS}
                    scrollRoot={scrollRef.current}
                    onRendered={handlePageRendered}
                  />
                ))}
              </div>
            </div>
            {showSummary && (
              <div className="pdf-modal-summary-popup">
                <span className="source-number">[{pdfModal.number}]</span>
                <span className="source-summary">{pdfModal.summary}</span>
                <button className="pdf-modal-summary-close" onClick={() => setShowSummary(false)}>&times;</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  const portfoliosContent = (
    <div className="portfolios-layout">
        <div className="portfolios-sidebar">
          <div className="portfolios-sidebar-header">
            <span style={{ fontWeight: 700, fontSize: 15 }}>Portfolios</span>
            {canUpload && !creatingPortfolio && (
              <button className="port-new-btn" onClick={() => setCreatingPortfolio(true)}>+ New</button>
            )}
          </div>
          {creatingPortfolio && (
            <form className="port-create-form" onSubmit={handleCreatePortfolio}>
              <input
                className="port-name-input"
                value={newPortfolioName}
                onChange={(e) => setNewPortfolioName(e.target.value)}
                placeholder="Portfolio name"
                autoFocus
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button type="submit" className="port-btn-primary" disabled={!newPortfolioName.trim()}>Create</button>
                <button type="button" className="port-btn-secondary" onClick={() => { setCreatingPortfolio(false); setNewPortfolioName(''); }}>Cancel</button>
              </div>
            </form>
          )}
          <ul className="port-list">
            {portfolios.filter(p => p.is_owner).map((p) => (
              <li
                key={p.id}
                className={`port-list-item ${mgmtPortfolioId === p.id ? 'port-list-item-active' : ''}`}
                onClick={() => setMgmtPortfolioId(p.id)}
              >
                <span className="port-list-name">{p.name}</span>
                {canUpload && (
                  <div className="port-list-actions" onClick={(e) => e.stopPropagation()}>
                    {isAdmin && (
                      <button
                        className={`port-public-btn ${p.is_public ? 'port-public-btn-on' : ''}`}
                        onClick={() => handleTogglePublic(p.id, p.is_public)}
                        title={p.is_public ? 'Make private' : 'Make public'}
                      >
                        {p.is_public ? '🌐' : '🔒'}
                      </button>
                    )}
                    <button
                      className="port-delete-btn"
                      onClick={() => handleDeletePortfolio(p.id)}
                      title="Delete portfolio"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </li>
            ))}
            {portfolios.filter(p => p.is_owner).length === 0 && (
              <li style={{ color: '#aaa', fontSize: 13, padding: '12px 8px' }}>
                {canUpload ? 'No portfolios yet. Create one above.' : 'No portfolios available.'}
              </li>
            )}
          </ul>
          {portfolios.some(p => !p.is_owner) && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#888', padding: '10px 8px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Public</div>
              <ul className="port-list">
                {portfolios.filter(p => !p.is_owner).map((p) => (
                  <li
                    key={p.id}
                    className={`port-list-item ${mgmtPortfolioId === p.id ? 'port-list-item-active' : ''}`}
                    onClick={() => setMgmtPortfolioId(p.id)}
                  >
                    <span className="port-list-name">{p.name}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="portfolios-main">
          {(() => {
            const mgmtPortfolio = portfolios.find((p) => p.id === mgmtPortfolioId);
            const canManage = canUpload && !!mgmtPortfolio?.is_owner;
            return !mgmtPortfolioId ? (
            <div className="chat-empty" style={{ marginTop: 80 }}>Select a portfolio to view its documents.</div>
          ) : (
            <>
              <div className="port-main-header">
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  {mgmtPortfolio?.name}
                </span>
                {canManage && (
                  <label className="port-upload-label">
                    Upload Document
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files[0];
                        if (f) { setUploadFile(f); setUploadName(f.name.replace(/\.[^.]+$/, '')); }
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
              {uploadFile && (
                <div className="port-upload-form">
                  <input
                    className="port-name-input"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder="Document name"
                  />
                  <button className="port-btn-primary" onClick={handleUpload} disabled={uploadLoading}>
                    {uploadLoading ? 'Uploading…' : `Upload "${uploadFile.name}"`}
                  </button>
                  <button className="port-btn-secondary" onClick={() => { setUploadFile(null); setUploadError(null); }}>Cancel</button>
                </div>
              )}
              {uploadError && <div className="error" style={{ margin: '8px 0' }}>{uploadError}</div>}
              {mgmtError && <div className="error">{mgmtError}</div>}
              <div className="port-doc-list">
                {mgmtLoading && (
                  <div className="port-doc-list-overlay">
                    <div className="pdf-page-spinner" />
                  </div>
                )}
                {mgmtDocuments.length === 0 && !mgmtLoading && (
                  <div className="chat-empty" style={{ marginTop: 40 }}>
                    {canManage ? 'No documents yet. Upload one above.' : 'No documents in this portfolio.'}
                  </div>
                )}
                {mgmtDocuments.length > 0 && (
                  <table className={`port-doc-table${mgmtLoading ? ' port-doc-item-dimmed' : ''}`}>
                    <thead>
                      <tr>
                        <th className="port-doc-th">Document</th>
                        <th className="port-doc-th" style={{ width: 72 }}>Status</th>
                        <th className="port-doc-th" style={{ width: 100 }}>Ingested</th>
                        <th className="port-doc-th" style={{ width: 60 }}>Pages</th>
                        <th className="port-doc-th" style={{ width: 100 }}>Version</th>
                        {canManage && <th className="port-doc-th" style={{ width: 40 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {mgmtDocuments.map((doc) => (
                        <tr key={doc.id} className="port-doc-row">
                          <td className="port-doc-td port-doc-title-cell">
                            <div className="port-doc-name">
                              {doc.shorthand
                                ? <><span className="port-doc-shorthand">{doc.shorthand}</span>: {doc.title || doc.name}</>
                                : (doc.title || doc.name)}
                            </div>
                            {doc.original_filename && (
                              <div className="port-doc-filename">{doc.original_filename}</div>
                            )}
                            {doc.status === 'error' && doc.log && (
                              <div className="port-doc-log" title={doc.log}>
                                {doc.log.length > 120 ? doc.log.slice(0, 120) + '…' : doc.log}
                              </div>
                            )}
                          </td>
                          <td className="port-doc-td port-doc-status-cell">
                            <span className={`port-doc-status port-status-${doc.status}`}>{doc.status}</span>
                          </td>
                          <td className="port-doc-td port-doc-date-cell">
                            {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="port-doc-td port-doc-date-cell">
                            {/\.pdf$/i.test(doc.original_filename || doc.name || '')
                              ? (doc.num_pages != null ? doc.num_pages : '—')
                              : 'n/a'}
                          </td>
                          <td className="port-doc-td port-doc-date-cell" title={doc.pipeline_version || ''}>
                            {doc.pipeline_version ? doc.pipeline_version.slice(0, 10) : '—'}
                          </td>
                          {canManage && (
                            <td className="port-doc-td port-doc-actions-cell">
                              <div>
                                {doc.can_retry && (doc.status === 'ready' || doc.status === 'error') && (
                                  <button
                                    className="port-reprocess-btn"
                                    onClick={() => handleRetryIngest(doc)}
                                    title="Re-process document"
                                  >
                                    ↻
                                  </button>
                                )}
                                <button
                                  className="port-delete-btn"
                                  onClick={() => handleDeleteDoc(doc.id)}
                                  disabled={doc.status === 'running'}
                                  title="Delete document"
                                >
                                  ✕
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          );
          })()}
        </div>
      </div>
  );

  const adminContent = (
    <div className="portfolios-page">
      <div className="portfolios-main">
        <div className="portfolios-main-header">
          <h2>Activity Log</h2>
          <button
            className="port-btn-primary"
            onClick={() => {
              setActivityLoading(true);
              setActivityError(null);
              authFetch('/admin/activity')
                .then((r) => r.json())
                .then((data) => { setActivityLog(data); setActivityLoading(false); })
                .catch((e) => { setActivityError(e.message); setActivityLoading(false); });
            }}
          >Refresh</button>
        </div>
        {activityLoading && <div className="portfolios-loading">Loading…</div>}
        {activityError && <div className="portfolios-error">{activityError}</div>}
        {!activityLoading && activityLog.length === 0 && !activityError && (
          <div style={{ color: '#888', padding: '16px 0' }}>Click Refresh to load activity.</div>
        )}
        {activityLog.length > 0 && (
          <div className="activity-log-table-wrap">
            <table className="activity-log-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Model</th>
                  <th>Credits</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {activityLog.map((entry) => {
                  const ts = new Date(entry.created_at).toLocaleString();
                  const meta = entry.meta
                    ? Object.entries(entry.meta).map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join('\n')
                    : '';
                  return (
                    <tr key={entry.id}>
                      <td className="al-ts">{ts}</td>
                      <td className="al-email">{entry.user_email}</td>
                      <td className="al-action">{entry.action}</td>
                      <td className="al-model">{entry.model || ''}</td>
                      <td className="al-credits">{entry.credits_used != null ? entry.credits_used : (entry.action === 'upload' ? 'N/A' : entry.action === 'search' ? '0' : '')}</td>
                      <td className="al-meta">{meta}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const adminUsersContent = (
    <div className="portfolios-page">
      <div className="portfolios-main">
        <div className="portfolios-main-header">
          <h2>Users</h2>
          <button
            className="port-btn-primary"
            onClick={() => {
              setAdminUsersLoading(true);
              setAdminUsersError(null);
              authFetch('/admin/users')
                .then((r) => r.json())
                .then((data) => { setAdminUsers(data); setAdminUsersLoading(false); })
                .catch((e) => { setAdminUsersError(e.message); setAdminUsersLoading(false); });
            }}
          >Refresh</button>
        </div>
        {adminUsersLoading && <div className="portfolios-loading">Loading…</div>}
        {adminUsersError && <div className="portfolios-error">{adminUsersError}</div>}
        {!adminUsersLoading && adminUsers.length === 0 && !adminUsersError && (
          <div style={{ color: '#888', padding: '16px 0' }}>Click Refresh to load users.</div>
        )}
        {adminUsers.length > 0 && (
          <div className="admin-users-list">
            {adminUsers.map((u) => (
              <div key={u.id} className="admin-user-row">
                <div className="admin-user-header">
                  <span className="admin-user-email">{u.email}</span>
                  {u.is_admin && <span className="admin-badge">admin</span>}
                  {u.can_upload && <span className="admin-badge upload-badge">upload</span>}
                  {u.credits != null && <span className="admin-credits">{u.credits.toLocaleString()} credits</span>}
                  <button className="port-btn-secondary admin-add-credits-btn" onClick={() => {
                    authFetch(`/admin/users/${u.id}/add_credits`, { method: 'POST' })
                      .then((r) => { if (!r.ok) throw new Error('Server error ' + r.status); return r.json(); })
                      .then((data) => {
                        if (data.credits != null) {
                          setAdminUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, credits: data.credits } : x));
                        }
                      })
                      .catch((e) => alert('Failed to add credits: ' + e.message));
                  }}>+1000</button>
                </div>
                {u.portfolios.length === 0 ? (
                  <div className="admin-user-empty">No portfolios</div>
                ) : (
                  <table className="admin-portfolio-table">
                    <thead>
                      <tr><th>Portfolio</th><th className="admin-col-center">Public</th><th className="admin-doc-count">Docs</th><th className="admin-date">First Upload</th><th className="admin-date">Last Upload</th></tr>
                    </thead>
                    <tbody>
                      {u.portfolios.map((p) => (
                        <tr key={p.id}>
                          <td>{p.name}</td>
                          <td className="admin-col-center">{p.is_public ? '✓' : ''}</td>
                          <td className="admin-doc-count">{p.doc_count}</td>
                          <td className="admin-date">{p.first_upload ? new Date(p.first_upload).toLocaleDateString() : '—'}</td>
                          <td className="admin-date">{p.last_upload ? new Date(p.last_upload).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={landingPage} />
      <Route path="/demo" element={appShell(<div className="app">{tabContent}</div>)} />
      <Route path="/chat" element={appShell(<div className="app">{tabContent}</div>)} />
      <Route path="/sufficiency" element={appShell(<div className="app">{tabContent}</div>)} />
      <Route path="/portfolios" element={appShell(portfoliosContent)} />
      {isAdmin && <Route path="/admin" element={appShell(adminContent)} />}
      {isAdmin && <Route path="/admin/users" element={appShell(adminUsersContent)} />}
      <Route path="*" element={landingPage} />
    </Routes>
  );
}

export default App;
